/**
 * App.tsx — the shell. Owns the high-level layout:
 *
 *   ┌─────────────────────────────────────────────┐
 *   │ MenuBar (File · View · Settings · About · ▶)│
 *   ├──────────────────────────────┬──────────────┤
 *   │ GraphCanvas                  │ Sidebar      │
 *   │ (nodes + animated edges)     │ (metrics +   │
 *   │                              │  balances +  │
 *   │                              │  legend)     │
 *   └──────────────────────────────┴──────────────┘
 *
 * Two entry modes from the Composer:
 *   - pick a template → opens it read-only (local compute, demo mode)
 *   - "Build from scratch" or "Edit a copy" → opens it editable (LocalAdapter)
 * In editable mode the graph editor, node/edge/metric editors all work and
 * knob changes affect the running sim.
 *
 * The compact desktop MenuBar (File/View/Settings/About + transport) is the
 * single home for view toggles, settings, file ops, and play/pause. View state
 * lives here: showSidebar, showGroupsPanel, showOverlays, showGrid, showArrows,
 * theme, plus the sim tick rate (hz) which is forwarded into useSim.
 */
import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { PencilSimple, Copy, Trash, ArrowsLeftRight, Plus, Plugs, TagSimple, Sun, Moon, X } from '@phosphor-icons/react';

import type { ScenarioBundle } from './engine/types';
import { hasBindings } from './engine/bindings';
import { useSim } from './useSim';
import { GraphCanvas, type GraphCanvasHandle } from './graph/GraphCanvas';
import { captureClipboard, pasteClipboard, type GraphClipboard } from './graph/clipboard';
import { useGlobalKeys } from './hooks/useGlobalKeys';
import { membersOf } from './graph/groups';
import { buildNodeScope } from './graph/scope';
import { Sidebar } from './panels/Sidebar';
import { MenuBar, type Theme } from './panels/MenuBar';
import { StatusBar } from './panels/StatusBar';
import { SettingsModal } from './panels/SettingsModal';
import { FormatPicker, type FormatOption } from './panels/FormatPicker';
// RelayBanner removed — the relay pulse lives in the StatusBar.
import { NodeModal } from './panels/NodeModal';
import { AgentModal } from './panels/AgentModal';
import { AgentsPanel } from './panels/AgentsPanel';
import { TriggersPanel } from './panels/TriggersPanel';
import { ServicesModal, StopServicesDialog } from './panels/ServicesModal';
import { UpdateModal } from './panels/UpdateModal';
import { GroupModal } from './panels/GroupModal';
import { EdgeModal } from './panels/EdgeModal';
import { DecisionsModal } from './panels/DecisionsModal';
import { MatrixCodegenModal } from './panels/MatrixCodegenModal';
import { agentDisplayName } from './overlays/annotations';
import { Modal } from './panels/Modal';
import { GroupsPanel } from './panels/GroupsPanel';
import { TablesPanel } from './panels/TablesPanel';
import FlowsDialog from './data/FlowsDialog';
import { Resizer } from './panels/Resizer';
import { Composer, emptyBundle } from './composer/Composer';
import { ErrorBoundary } from './ErrorBoundary';
import { ConfirmProvider, useConfirm } from './panels/ConfirmDialog';
import { PromptProvider, usePrompt } from './panels/PromptDialog';
import { stopModelAndPromptOthers } from './code/taskStop';

// Code tab — lazy-loaded so the Monaco + jupyter bundles (large) are only
// fetched when a user actually switches to the Code tab. The View tab never
// pays for them. The Code tab is a single surface: a notebook (.ipynb) is a
// file that opens in a tab like any other, so there's no separate notebook
// panel at the shell level.
const CodePanel = lazy(() => import('./code/CodePanel').then((m) => ({ default: m.CodePanel })));
// PlotPanel is lazy for the same reason as CodePanel: it pulls in
// react-grid-layout + (transitively, per tile kind) plotly.js / vega, none of
// which the View tab needs. Splitting here keeps the View tab's bundle lean.
const PlotPanel = lazy(() => import('./plot/PlotPanel').then((m) => ({ default: m.PlotPanel })));
// DataPanel (the Data tab's records grid + SQL console) is lazy to keep the
// View tab lean; it's plain fetch + DOM (no heavy editor deps).
const DataPanel = lazy(() => import('./data/DataPanel'));
// WorkflowPanel (the Workflows tab) is lazy for the same reason: it pulls in
// the React Flow canvas, which the View tab already has but data/plot don't.
const WorkflowPanel = lazy(() => import('./workflow/WorkflowPanel'));
// ChatModal is lazy for the same reason: it pulls in @assistant-ui/react
// (the chat runtime + primitives). The View/Plot/Code tabs never import it.
const ChatModal = lazy(() => import('./chat/ChatModal').then((m) => ({ default: m.ChatModal })));
import type { PlotPanelHandle } from './plot/PlotPanel';
import type { CodePanelHandle } from './code/CodePanel';
import type { WorkflowPanelHandle } from './workflow/WorkflowPanel';
// The Workflows tab's own bottom rail (grid/minimap + status dots). Tiny +
// dependency-free, so it's eager (unlike WorkflowPanel's canvas chunk).
import { WorkflowStatusBar } from './workflow/WorkflowStatusBar';
import type { OpenFile } from './code/FileTabs';
import { useAiConfig, resolveChoice } from './chat/aiConfig';
import { AskBox } from './ask/AskBox';
import { activeInsertTarget } from './ask/registry';
import { viewContextSnapshot } from './chat/workspaceContext';
import { useRunTaskModal, RunTaskModal } from './code/RunTaskModal';
import { LibraryModal } from './panels/LibraryModal';
import { ContextMenu, type ContextMenuState } from './panels/ContextMenu';
import { getDefaultStorage } from './storage/LocalStorageBackend';
import type { StorageBackend } from './storage/StorageBackend';
import { usePersistedState } from './storage/usePersistedState';
import { httpOriginOf } from './connect/httpOrigin';
import { fireWorkflow } from './workflow/workflowApi';
import { ConnectivityProvider } from './connect/useConnectivity';
import { VersionProvider, useVersionCheck } from './version/useVersionCheck';
import { appVersion } from './version/manifest';
import { LocalLibraryAdapter } from './library/LocalLibraryAdapter';
import { RemoteProjectAdapter, type DatasetRunInfo } from './projects/RemoteProjectAdapter';
import { ReplayPanel } from './replay/ReplayPanel';
import { ReplayPicker, docFromIndex, docFromRun } from './replay/ReplayPicker';
import { defaultRunName } from './replay/logic';
import type { ReplayDoc, ReplayIndexEntry, ReplayTab } from './replay/types';
import type { ReplaySession } from './replay/useReplaySession';
import { uuid } from './library/ids';
import type { LibraryAdapter, LibraryEntry } from './library/LibraryAdapter';
import { LocalDocumentAdapter } from './documents/LocalDocumentAdapter';
import type { DocumentAdapter } from './documents/DocumentAdapter';
import { useDocument } from './documents/useDocument';
import { useAuth } from './auth/AuthContext';
import { LicenseGate } from './license/LicenseGate';

/** One storage instance for the app lifetime. Wrap in usePersistedState for
 *  individual prefs; the library + documents go through their async ports. */
const storage: StorageBackend = getDefaultStorage();

/** The library adapter — the single async port for library CRUD + search.
 *  `LocalLibraryAdapter` today (browser localStorage); swap for
 *  `RemoteLibraryAdapter` when running against the localhost/SaaS backend
 *  (`/api/library`). Both UI surfaces (start page + File→Library picker)
 *  consume this opaquely. Demo/template projects are ordinary entries —
 *  the backend seeds them into the user's project data dir on first run. */
const libraryAdapter: LibraryAdapter = new LocalLibraryAdapter(storage);

/** The local document adapter — mints doc ids on the frontend and stores the
 *  bundle under `bs.bundle.<docId>` (the same key `LazyLocalAdapter` reads
 *  from, so the sim picks the opened doc up without a second copy). */
const localDocumentAdapter: DocumentAdapter = new LocalDocumentAdapter({ storage });

/** How a Composer entry becomes a route. The URL suffix picks the mode:
 *  `/doc/{id}/edit` (full editor) or `/doc/{id}/view` (read-only viewer). */
export type OpenMode = 'edit' | 'view';

/** The doc access mode — the single source of truth for whether the editor,
 *  metric add, node/edge editing, etc. are enabled. 'view' disables all of it. */
export type DocMode = 'edit' | 'view';

export function App(): JSX.Element {
  return (
      <ConnectivityProvider>
        <VersionProvider>
        <ConfirmProvider>
          <PromptProvider>
            {/* The license gate — blocks the app behind an opaque modal until
                the license is accepted (recorded in a cookie that all API
                requests then carry). Renders null once accepted. */}
            <LicenseGate />
            <Routes>
              <Route path="/" element={<StartPage />} />
              {/* Bare /doc/:docId defaults to edit. */}
              <Route path="/doc/:docId" element={<Navigate to="edit" replace />} />
              <Route path="/doc/:docId/edit" element={<DocRoute mode="edit" />} />
              <Route path="/doc/:docId/view" element={<DocRoute mode="view" />} />
              <Route path="*" element={<StartPage />} />
            </Routes>
          </PromptProvider>
        </ConfirmProvider>
        </VersionProvider>
      </ConnectivityProvider>
  );
}

/** The start page. Buttons that open a document navigate to `/doc/:id` instead
 *  of flipping in-memory state — the URL is the source of truth. */
function StartPage(): JSX.Element {
  const navigate = useNavigate();
  const auth = useAuth();
  const [theme, setTheme] = usePersistedState<'dark' | 'light'>(storage, 'theme', 'dark');
  // Apply the theme on the start page too — without this, the floating toggle
  // only persisted the pref and <html data-theme> stayed stale until a Studio
  // mounted (its own effect was the only place the attribute was written).
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  // When connected to a backend (auth.wsUrl set), the Library reads from the
  // backend's `/api/projects` (RemoteProjectAdapter — the unified project
  // surface that replaces the old library/documents split). Otherwise it reads
  // from localStorage (LocalLibraryAdapter). The user picks a real project by
  // its id — never a hardcoded scenario slug.
  const activeLibrary = useMemo<LibraryAdapter>(
    () =>
      auth.wsUrl
        ? new RemoteProjectAdapter({
            baseUrl: httpOriginOf(auth.wsUrl, '/api/projects'),
            apiKey: auth.token ?? undefined,
          })
        : libraryAdapter,
    [auth.wsUrl, auth.token],
  );

  return (
    <>
      <Composer
        adapter={activeLibrary}
        onOpenEntry={async (entry: LibraryEntry, mode: OpenMode) => {
        // When opening a remote project, fetch its bundle and stash it locally
        // so the DocRoute (which uses the local document adapter in API-connect
        // mode) can resolve it by the entry's id. The bundle's internal id may
        // differ from the project id (e.g. graph.json has "ghost_kitchen" but
        // the project id is "ghost-kitchen"); rewrite it so the storage key
        // matches the route. A failed fetch (broken graph 4xx/5xx, network)
        // must NOT block opening: stash an empty bundle so the doc mounts —
        // the WS handshake (or its error frame) is the source of truth once
        // Studio is up, and the failure renders in the canvas error banner.
        if (auth.wsUrl) {
          const bundle = await activeLibrary.get(entry.id).catch((e: unknown) => {
            console.warn(`graph fetch for '${entry.id}' failed:`, e);
            return null;
          });
          storage.saveBundle(
            bundle ? { ...bundle, id: entry.id } : { ...emptyBundle(), id: entry.id },
          );
        }
        navigate(`/doc/${entry.id}/${mode}`);
      }}
      onBlank={async (name: string) => {
        // New: a fresh blank project. The backend mints the id from the NAME
        // (slugified, `-2`/`-3`… on collision — no uuids) and scaffolds the
        // whole project: a manifest with every feature surface enabled
        // (notebook/tables/plots/workflows/trainers/models + install), an
        // empty Main graph, data/tables.json, and the project's
        // docker-compose.yml. The prompted name is the display label.
        if (auth.wsUrl) {
          if (typeof activeLibrary.createBlank === 'function') {
            try {
              const entry = await activeLibrary.createBlank(name);
              const bundle = await activeLibrary.get(entry.id);
              if (bundle) storage.saveBundle({ ...bundle, id: entry.id });
              navigate(`/doc/${entry.id}/edit`);
              return;
            } catch {
              // Fall through to the bundle-upload path (older backend).
            }
          }
          const bundle = { ...emptyBundle(), label: name };
          try {
            const entry = await activeLibrary.importBundle(bundle, { slug: name });
            storage.saveBundle({ ...bundle, id: entry.id });
            navigate(`/doc/${entry.id}/edit`);
          } catch {
            // Upload failed — fall back to a local doc so the button isn't dead.
            const { docId } = await localDocumentAdapter.create(bundle);
            navigate(`/doc/${docId}`);
          }
        } else {
          const bundle = { ...emptyBundle(), label: name };
          const { docId } = await localDocumentAdapter.create(bundle);
          navigate(`/doc/${docId}/edit`);
        }
      }}
      />
      <button
        type="button"
        className="btn-icon-mini"
        style={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000,
          background: 'var(--panel-2)',
          border: '1px solid var(--border)',
          borderRadius: '50%',
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
        title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      >
        {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
      </button>
    </>
  );
}

/** The `/doc/:docId/{edit|view}` route. Resolves the document adapter (local vs
 *  remote from `useAuth()`), drives `useDocument` for the loading state, then
 *  renders the `<Studio>` once the authoritative bundle is in hand. `mode`
 *  flows down as the single read-only/edit gate. */
function DocRoute({ mode }: { mode: DocMode }): JSX.Element {
  const { docId = '' } = useParams<{ docId: string }>();
  const auth = useAuth();
  // The document adapter resolves the bundle for rendering. With the unified
  // project surface, the bundle is always stashed locally before navigation
  // (the onOpenEntry handler fetches it from /api/projects/{id}/graph and
  // saves it to localStorage). So we always use the local adapter — the
  // old RemoteDocumentAdapter (collaborative editing over /api/documents)
  // is gone, replaced by the project download/upload flow.
  const documentAdapter = localDocumentAdapter;
  // Same library-adapter switch as StartPage: when connected to a backend
  // (auth.wsUrl set), the File→Library picker reads from the backend's
  // /api/projects surface (RemoteProjectAdapter).
  const activeLibrary = useMemo<LibraryAdapter>(
    () =>
      auth.wsUrl
        ? new RemoteProjectAdapter({
            baseUrl: httpOriginOf(auth.wsUrl, '/api/projects'),
            apiKey: auth.token ?? undefined,
          })
        : libraryAdapter,
    [auth.wsUrl, auth.token],
  );
  const { session } = useDocument(documentAdapter, docId);

  if (session.status === 'loading' || !session.bundle) {
    return (
      <div className="app-shell">
        <div className="loading">Loading document…</div>
      </div>
    );
  }
  if (session.status === 'error') {
    return (
      <div className="app-shell">
        <div className="error-banner" style={{ margin: 24 }}>
          <strong>Couldn't open this document.</strong>
          <pre>{session.error ?? 'Unknown error.'}</pre>
          <a className="btn" href="/">← Back to start</a>
        </div>
      </div>
    );
  }
  return (
    <Studio
      key={docId}
      docId={docId}
      mode={mode}
      initialBundle={session.bundle}
      storage={storage}
      libraryAdapter={activeLibrary}
      documentAdapter={documentAdapter}
    />
  );
}

function Studio({
  docId,
  mode,
  initialBundle,
  storage,
  libraryAdapter,
  documentAdapter,
}: {
  docId: string;
  mode: DocMode;
  initialBundle: ScenarioBundle;
  storage: StorageBackend;
  libraryAdapter: LibraryAdapter;
  documentAdapter: DocumentAdapter;
}): JSX.Element {
  // The doc is already open + authoritative via DocRoute's `useDocument`. The
  // sim drives the live ticks; `LazyLocalAdapter(scenarioId=docId)` reads the
  // bundle back out of localStorage (where the document adapter put it), so the
  // sim and the document session share one source of truth.
  const navigate = useNavigate();
  const auth = useAuth();
  // When the user came in via Connect API, the WS URL + relay flag are stashed
  // in AuthContext. The Studio hands them to useSim so the ApiAdapter talks to
  // the same backend (and, in relay mode, listens for external POSTs).
  const apiSource = !!auth.wsUrl;
  const apiRelay = auth.relay;
  // The URL mode is the single source of truth for read-only vs editor. Every
  // component already gates on `editable`/`canEdit` (graph interactions, node/
  // edge modals, the sidebar "+ metric" button, MenuBar file ops), so flipping
  // this one flag turns the editor into a viewer.
  const editable = mode === 'edit';
  // Tick rate forwarded into useSim → adapter. Declared before useSim because
  // the adapter constructor reads it. Changing it reloads the adapter (useSim's
  // load effect deps include hz), which resets the sim to step 0 — simplest
  // correct behavior, matches a fresh scenario load. Persisted across reloads.
  const [hz, setHz] = usePersistedState(storage, 'hz', 4);
  // Seed the sim's localStorage copy BEFORE useSim's passive load effect runs.
  // useLayoutEffect fires synchronously after render but before passive
  // effects, so `LazyLocalAdapter` (constructed in useSim) is guaranteed to
  // find the bundle `useDocument` resolved — never falling through to a fetch.
  // For a locally-created doc the document adapter already wrote it; for a
  // built-in template or remote doc this is the authoritative seed.
  useLayoutEffect(() => {
    if (!storage.loadBundle(docId)) storage.saveBundle(initialBundle);
  }, [docId, initialBundle]);
  const sim = useSim(docId, {
    source: apiSource ? 'api' : 'local',
    relay: apiRelay,
    apiUrl: auth.wsUrl ?? undefined,
    apiKey: auth.token ?? undefined,
    editable,
    hz,
  });

  // ⚡ Fire a workflow run from a UI control (TriggersPanel Fire-now, the
  // NodeModal's trigger rows, the Workflows rail). Worker-ensure + run start
  // through the same REST surface the Workflows tab uses; failures reject
  // with a readable reason the calling surface shows inline.
  const fireTrigger = useCallback(
    async (workflow: string, input: unknown[]) => {
      if (!auth.wsUrl) throw new Error('Not connected to a backend.');
      await fireWorkflow(
        { origin: httpOriginOf(auth.wsUrl, ''), token: auth.token ?? undefined, projectId: docId },
        workflow,
        input,
      );
    },
    [auth.wsUrl, auth.token, docId],
  );

  // SELECTION drives canvas highlight + sidebar row; OPEN drives the modal.
  // They are deliberately separate: creating a node/group selects it (so it
  // lights up + the sidebar scrolls to it) without popping a modal. The modal
  // opens only on double-click or via the context menu's "Edit" entry.
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null);
  // Spatial node group selection — highlights the rect + selects its sidebar row.
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  // The id whose modal is currently open. Null = no modal.
  const [openNode, setOpenNode] = useState<string | null>(null);
  const [openEdge, setOpenEdge] = useState<string | null>(null);
  const [edgeModalTab, setEdgeModalTab] = useState<'settings' | 'params'>('settings');
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  // Agent modal opened standalone (from a context menu), independent of the
  // node/edge modal. Either openAgentNode or openAgentEdge is set.
  const [openAgentNode, setOpenAgentNode] = useState<string | null>(null);
  const [openAgentEdge, setOpenAgentEdge] = useState<string | null>(null);
  // The trigger contract's canvas badge source: node ids a bundle-level
  // workflowTrigger sources (⚡). Memoised so the nodes memo deps stay stable.
  const workflowTriggerLinks = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const t of sim.bundle?.workflowTriggers ?? []) {
      const src = t.sourceNode ? `⚡ node ${t.sourceNode}`
        : t.sourceAgent ? `agent ${t.sourceAgent}`
        : 'project';
      const line = t.on === 'expr' && t.expr
        ? `${src}: ${t.expr}`
        : t.on === 'cron' && t.schedule
          ? `${src} (cron ${t.schedule})`
          : `${src} (manual)`;
      (out[t.workflow] ??= []).push(line);
    }
    return out;
  }, [sim.bundle?.workflowTriggers]);
  const workflowTriggerNodes = useMemo(
    () => new Set((sim.bundle?.workflowTriggers ?? [])
      .map((t) => t.sourceNode)
      .filter((id): id is string => typeof id === 'string')),
    [sim.bundle?.workflowTriggers],
  );
  /** Whether any agents are configured anywhere in the project (the global
   *  list, node configs, edge configs) — gates the transport's agents
   *  kill-switch toggle (no agents → nothing to kill). */
  const hasAgents = useMemo(
    () => sim.bundles.some(
      (b) =>
        (b.agents?.length ?? 0) > 0 ||
        b.graph.nodes.some((n) => n.agentConfig) ||
        b.graph.edges.some((e) => e.agentConfig),
    ),
    [sim.bundles],
  );
  // ⚡ badge state: a node whose triggers are ALL disarmed shows a dimmed ⚡;
  // a node any of whose triggers fired (or failed) in the last 4s pulses.
  // Derived from the bundle (armed flags) + the live triggerStatus map fed
  // by `triggerEvent` broadcasts.
  const workflowTriggerDisarmed = useMemo(
    () => {
      const byNode: Record<string, { total: number; disarmed: number }> = {};
      for (const t of sim.bundle?.workflowTriggers ?? []) {
        if (!t.sourceNode) continue;
        const e = (byNode[t.sourceNode] ??= { total: 0, disarmed: 0 });
        e.total += 1;
        if (t.enabled === false) e.disarmed += 1;
      }
      return new Set(Object.entries(byNode)
        .filter(([, e]) => e.disarmed >= e.total)
        .map(([id]) => id));
    },
    [sim.bundle?.workflowTriggers],
  );
  const [workflowTriggerFlash, setWorkflowTriggerFlash] = useState<Set<string>>(new Set());
  useEffect(() => {
    return sim.onTriggerEvent((msg) => {
      // Map the firing trigger id back onto its source node (bundle lookup);
      // agent-path events (id "agent:<wf>") have no node — flash nothing.
      const trig = (sim.bundle?.workflowTriggers ?? []).find((t) => t.id === msg.triggerId);
      const nodeId = trig?.sourceNode;
      if (!nodeId) return;
      setWorkflowTriggerFlash((cur) => new Set(cur).add(nodeId));
      window.setTimeout(() => {
        setWorkflowTriggerFlash((cur) => {
          const next = new Set(cur);
          next.delete(nodeId);
          return next;
        });
      }, 4000);
    });
  }, [sim.onTriggerEvent, sim.bundle?.workflowTriggers]);
  // Imperative canvas surface for the keyboard bindings (multi-selection reads
  // + viewport controls — see GraphCanvasHandle) and the internal copy/paste
  // clipboard. Refs, not state: the key handlers read them at event time.
  const canvasRef = useRef<GraphCanvasHandle | null>(null);
  const clipboardRef = useRef<GraphClipboard | null>(null);
  const pasteCountRef = useRef(0);
  // Prefs persist across reloads via the storage layer (localStorage by default).
  const [showOverlays, setShowOverlays] = usePersistedState(storage, 'showOverlays', true);
  const [showArrows, setShowArrows] = usePersistedState(storage, 'showArrows', true);
  const [theme, setTheme] = usePersistedState<Theme>(storage, 'theme', 'dark');
  const [menu, setMenu] = useState<ContextMenuState | null>(null);
  // #4 — Groups panel state: which groups are hidden, which are highlighted,
  // which is being hover-previewed. Plus the panel + grid visibility toggles.
  const [hiddenGroups, setHiddenGroups] = useState<Set<string>>(new Set());
  const [highlightedGroups, setHighlightedGroups] = useState<Set<string>>(new Set());
  const [hoverGroup, setHoverGroup] = useState<string | null>(null);
  const [showGroupsPanel, setShowGroupsPanel] = usePersistedState(storage, 'showGroupsPanel', false);
  const [showTablesPanel, setShowTablesPanel] = usePersistedState(storage, 'showTablesPanel', false);
  const [showDataSidebar, setShowDataSidebar] = usePersistedState(storage, 'showDataSidebar', true);
  const [showCodeSidebar, setShowCodeSidebar] = usePersistedState(storage, 'showCodeSidebar', true);
  const [showWorkflowRail, setShowWorkflowRail] = usePersistedState(storage, 'showWorkflowRail', true);
  // Per-tab left-sidebar widths — the graph view's sidebarWidth pattern: the
  // App owns the persisted width, the panel renders the Resizer handle.
  const [dataSidebarWidth, setDataSidebarWidth] = usePersistedState(storage, 'dataSidebarWidth', 200);
  const [dataSchemaWidth, setDataSchemaWidth] = usePersistedState(storage, 'dataSchemaWidth', 180);
  const [plotRailWidth, setPlotRailWidth] = usePersistedState(storage, 'plotRailWidth', 240);
  const [codeSidebarWidth, setCodeSidebarWidth] = usePersistedState(storage, 'codeSidebarWidth', 240);
  const [workflowRailWidth, setWorkflowRailWidth] = usePersistedState(storage, 'workflowRailWidth', 230);
  // View/Code shell switch. 'code' is only meaningful for backend projects
  // (canRemote); local mode has no project files to edit. Persisted so a
  // reload keeps the user in the tab they chose. There is no Files|Notebook
  // sub-state — the Code tab is one surface and a notebook is just a file.
  // View | Data | Plot | Code | Workflows - the top-level modes. Data/Plot/
  // Code/Workflows are backend-project-only (gated by canRemote). Data is the
  // full data-management surface (records grid + SQL console); Plot is a
  // dashboard builder fed by the project's plots/ registry; Code is the Monaco
  // + notebook editor; Workflows renders durable workflows (code-derived
  // graphs + run control).
  const [appView, setAppView] = usePersistedState<'view' | 'data' | 'plot' | 'code' | 'workflows'>(
    storage,
    'appView',
    'view',
  );

  const [visitedCode, setVisitedCode] = useState(appView === 'code');
  const [visitedPlot, setVisitedPlot] = useState(appView === 'plot');
  const [visitedData, setVisitedData] = useState(appView === 'data');
  const [visitedWorkflows, setVisitedWorkflows] = useState(appView === 'workflows');
  useEffect(() => {
    if (appView === 'code') setVisitedCode(true);
    if (appView === 'plot') setVisitedPlot(true);
    if (appView === 'data') setVisitedData(true);
    if (appView === 'workflows') setVisitedWorkflows(true);
  }, [appView]);

  // Tables refresh poll: the running model mutates rows behind the grid, so
  // refresh ONLY while the dock is visible. Db knobs do NOT keep the poll
  // alive: they pull tables once on mount (Sidebar) and stay fresh through
  // edit splices — a closed panel polling forever was pure request noise
  // (none of the pinned cells are model-mutated). Cell BINDINGS are the
  // exception: a bound cell drives a graph param and IS model/agent-mutated
  // while nobody watches, so bindings keep the poll alive. useSim backs off
  // right after a mutation.
  const setTablesPolling = sim.setTablesPolling;
  const simMode = sim.mode;
  const graphHasBindings = useMemo(
    () => hasBindings(sim.bundles ?? []),
    [sim.bundles],
  );
  useEffect(() => {
    setTablesPolling(
      (showTablesPanel || graphHasBindings) && (simMode === 'api' || simMode === 'relay'),
    );
  }, [showTablesPanel, graphHasBindings, simMode, setTablesPolling]);
  // First pull when bindings exist (the poll above only refreshes; the local
  // engine needs a snapshot before it can resolve anything). Skips cleanly
  // once a snapshot is in place.
  const simTablesState = sim.tablesState;
  const simLoadTables = sim.loadTables;
  useEffect(() => {
    if (graphHasBindings && !simTablesState) void simLoadTables?.();
  }, [graphHasBindings, simTablesState, simLoadTables]);

  // Dirty-file count lifted from the Code panel so the MenuBar's Save button
  // can show "Save N" without the MenuBar knowing about the editor internals.
  // The ref lets the MenuBar button call into the panel's `saveAll`.
  const codePanelRef = useRef<CodePanelHandle | null>(null);
  const [codeDirtyCount, setCodeDirtyCount] = useState(0);
  // Open-file tabs lifted from the Code panel so the MenuBar can render them
  // inline (after the View/Code switcher) instead of a separate tab row. The
  // tab PATHS + active path persist per-project (localStorage) so switching
  // View↔Code (which unmounts/remounts CodePanel) restores the open tabs —
  // only the identities persist; content is re-fetched on restore.
  const [codeTabs, setCodeTabs] = useState<OpenFile[]>([]);
  const [codeActiveTab, setCodeActiveTab] = useState<string | null>(null);
  const codeTabsKey = `codeTabs:${docId}`;
  const [savedCodeTabs, setSavedCodeTabs] = usePersistedState<string[]>(
    storage, codeTabsKey, [],
  );
  const codeActiveKey = `codeActiveTab:${docId}`;
  const [savedCodeActive, setSavedCodeActive] = usePersistedState<string | null>(
    storage, codeActiveKey, null,
  );
  /** Stable mirror callback: CodePanel reports (tabs, activePath) and App
   *  stores both — the in-memory copy (for the MenuBar) + the persisted paths
   *  (for restore on remount). Memoized so CodePanel's onTabsChange effect
   *  doesn't loop. */
  const setCodeTabsMirror = useCallback(
    (tabs: OpenFile[], activePath: string | null) => {
      setCodeTabs(tabs);
      setCodeActiveTab(activePath);
      // Persist identities only (paths); content/dirty are ephemeral.
      const paths = tabs.map((t) => t.path);
      // Avoid writing if unchanged (keeps the persisted array stable).
      setSavedCodeTabs((prev) => (prev.length === paths.length && prev.every((p, i) => p === paths[i]) ? prev : paths));
      setSavedCodeActive((prev) => (prev === activePath ? prev : activePath));
    },
    [setSavedCodeTabs, setSavedCodeActive],
  );
  // Plot panel: imperative handle (dashboard create/rename/delete/select driven
  // from the MenuBar's dashboard tabs) + lifted dashboard-list/active state so
  // the MenuBar can render the tabs inline (after the View switcher), mirroring
  // how code file-tabs work. The rail (registry) toggle is persisted per-project.
  const plotPanelRef = useRef<PlotPanelHandle | null>(null);
  const [plotDashboards, setPlotDashboards] = useState<{ id: string; name: string }[]>([]);
  const [plotActiveDashboard, setPlotActiveDashboard] = useState<string | null>(null);
  const [showPlotRail, setShowPlotRail] = usePersistedState(storage, `plotRail:${docId}`, true);
  const runTask = useRunTaskModal();
  // Workflows panel: imperative handle (tab strip driven from the MenuBar) +
  // the lifted tab mirror + the canvas toggles the WorkflowStatusBar owns.
  const workflowPanelRef = useRef<WorkflowPanelHandle | null>(null);
  const [workflowTabs, setWorkflowTabs] = useState<{ id: string; name: string; error?: boolean }[]>([]);
  const [workflowActiveTab, setWorkflowActiveTab] = useState<string | null>(null);
  // The Workflows tab's edit history reachability — mirrors Ctrl+Z state into
  // the menu bar's Undo/Redo entries while that tab is active.
  const [wfHistory, setWfHistory] = useState({ canUndo: false, canRedo: false });
  // Open workflow tabs persist per-project so a reload reopens them (the
  // codeTabs pattern: identities only — the structures re-derive from code).
  const [savedWfTabs, setSavedWfTabs] = usePersistedState<string[]>(storage, `wfTabs:${docId}`, []);
  const [savedWfActive, setSavedWfActive] = usePersistedState<string | null>(
    storage, `wfActiveTab:${docId}`, null,
  );
  const setWorkflowTabsMirror = useCallback(
    (tabs: { id: string; name: string; error?: boolean }[], activeId: string | null) => {
      setWorkflowTabs(tabs);
      setWorkflowActiveTab(activeId);
      setSavedWfTabs((prev) =>
        prev.length === tabs.length && prev.every((p, i) => p === tabs[i].id) ? prev : tabs.map((t) => t.id),
      );
      setSavedWfActive((prev) => (prev === activeId ? prev : activeId));
    },
    [setSavedWfTabs, setSavedWfActive],
  );
  // Deep-link workflow for the Code tab's backlink button: set by
  // openWorkflowTab, opened once the WorkflowPanel has mounted (its chunk is
  // lazy — the ref only populates after visiting Workflows; retry briefly,
  // the pendingCodeFile recipe).
  const [pendingWorkflow, setPendingWorkflow] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingWorkflow) return;
    if (workflowPanelRef.current) {
      workflowPanelRef.current.selectWorkflow(pendingWorkflow);
      setPendingWorkflow(null);
      return;
    }
    const started = Date.now();
    const t = window.setInterval(() => {
      if (workflowPanelRef.current) {
        workflowPanelRef.current.selectWorkflow(pendingWorkflow);
        setPendingWorkflow(null);
      } else if (Date.now() - started > 10_000) {
        setPendingWorkflow(null); // panel never mounted — drop the link
      }
    }, 200);
    return () => window.clearInterval(t);
  }, [pendingWorkflow]);
  /** Open a workflow in the Workflows tab (the Code editor's backlink for
   *  workflow modules): switch views first, then select once mounted. */
  const openWorkflowTab = useCallback((stem: string) => {
    setAppView('workflows');
    setPendingWorkflow(stem);
  }, [setAppView]);
  const [wfRunState, setWfRunState] = useState<{ runStatus: string | null; workerRunning: boolean }>({
    runStatus: null,
    workerRunning: false,
  });
  const setWfRunStateMirror = useCallback(
    (state: { runStatus: string | null; workerRunning: boolean }) =>
      setWfRunState((prev) =>
        prev.runStatus === state.runStatus && prev.workerRunning === state.workerRunning ? prev : state,
      ),
    [],
  );
  const [wfShowGrid, setWfShowGrid] = usePersistedState(storage, 'wfShowGrid', false);
  const [wfShowMinimap, setWfShowMinimap] = usePersistedState(storage, 'wfShowMinimap', false);
  // The active GRAPH tab persists per-project so a reload reopens it (the
  // sim's activeGraphId is in-memory only; seeded back once bundles load).
  const [savedActiveGraph, setSavedActiveGraph] = usePersistedState<string | null>(
    storage, `activeGraphId:${docId}`, null,
  );
  const seededActiveGraphRef = useRef(false);
  const simSetActiveGraph = sim.setActiveGraph;
  const simActiveGraphId = sim.activeGraphId;
  useEffect(() => {
    if (seededActiveGraphRef.current || !sim.bundle) return;
    seededActiveGraphRef.current = true;
    if (savedActiveGraph && savedActiveGraph !== simActiveGraphId
        && (sim.bundles ?? []).some((b) => b.id === savedActiveGraph)) {
      simSetActiveGraph?.(savedActiveGraph);
    }
  }, [sim.bundle, sim.bundles, savedActiveGraph, simActiveGraphId, simSetActiveGraph]);
  // ── Replay tabs (read-only recorded periods — src/replay/). Transient
  // session documents in the View tab's graph strip (closable ×), unlike the
  // pinned graph tabs. Each tab owns its ReplayPanel (frozen canvas + the
  // timeline bar that replaces the StatusBar while active); inactive tabs
  // stay mounted display:none so their cursor/window cache survives
  // switching. The keyboard layer reads the ACTIVE tab's session through a
  // ref (registered by the panel each render — no state mirror loop).
  const [replayTabs, setReplayTabs] = useState<ReplayTab[]>([]);
  const [activeReplayKey, setActiveReplayKey] = useState<string | null>(null);
  const [showReplayPicker, setShowReplayPicker] = useState(false);
  const [replayPickerRuns, setReplayPickerRuns] = useState<DatasetRunInfo[]>([]);
  const [replayPickerReplays, setReplayPickerReplays] = useState<ReplayIndexEntry[]>([]);
  const [replayPickerLoading, setReplayPickerLoading] = useState(false);
  const [replayPickerError, setReplayPickerError] = useState<string | null>(null);
  const activeReplaySessionRef = useRef<ReplaySession | null>(null);
  const replayKeySeq = useRef(0);

  const projectAdapter = libraryAdapter instanceof RemoteProjectAdapter ? libraryAdapter : null;
  const replayActive = appView === 'view' && activeReplayKey !== null;

  /** The chat agent's "where the user is" snapshot, read at SEND time: active
   *  view, open/active artifacts, selections/modals. Composes the state App
   *  already mirrors; panels supplement theirs via registerViewContext (see
   *  src/chat/workspaceContext.ts). Best-effort — the chat works without it. */
  const chatWorkspaceContext = useCallback((): Record<string, unknown> | null => {
    const base: Record<string, unknown> = { view: appView };
    if (appView === 'view') {
      const bundle = (sim.bundles ?? []).find((b) => b.id === sim.activeGraphId);
      if (bundle) base.graph = bundle.label || bundle.id;
      // Every view names the FILE behind it — the agent reads/edits project
      // files by path (graphs/<id>.json, dashboards/<id>.json, …).
      if (sim.activeGraphId) base.graphFile = `graphs/${sim.activeGraphId}.json`;
      const graph = sim.bundle?.graph;
      const node = openNode ? graph?.nodes.find((n) => n.id === openNode) : null;
      const edge = openEdge ? graph?.edges.find((e) => e.id === openEdge) : null;
      const selNode = !node && selectedNode ? graph?.nodes.find((n) => n.id === selectedNode) : null;
      const selEdge = !edge && selectedEdge ? graph?.edges.find((e) => e.id === selectedEdge) : null;
      if (node) base.nodeModal = { id: node.id, label: node.label };
      else if (selNode) base.selectedNode = { id: selNode.id, label: selNode.label };
      if (edge) base.edgeModal = { id: edge.id, source: edge.source, target: edge.target };
      else if (selEdge) base.selectedEdge = { id: selEdge.id, source: selEdge.source, target: selEdge.target };
      if (replayActive) base.replaying = activeReplayKey;
    }
    if (appView === 'code') {
      base.openFiles = codeTabs.map((t) => (t.dirty ? `${t.path} (unsaved)` : t.path));
      if (codeActiveTab) base.activeFile = codeActiveTab;
      // The focused editor's selection (path + line numbers + text) via the
      // AskBox registry — Monaco registers on focus.
      const target = activeInsertTarget();
      if (target?.kind === 'monaco') {
        const sel = target.getSelection();
        if (sel?.text) {
          base.selection = { file: sel.file, lines: sel.lines ?? null, text: sel.text.slice(0, 400) };
        }
      }
    }
    if (appView === 'plot') {
      const dash = plotDashboards.find((d) => d.id === plotActiveDashboard);
      if (dash) base.dashboard = dash.name;
      if (plotActiveDashboard) base.dashboardFile = `dashboards/${plotActiveDashboard}.json`;
    }
    if (appView === 'workflows') {
      base.openWorkflows = workflowTabs.map((t) => `workflows/${t.id}.py`);
      const active = workflowTabs.find((t) => t.id === workflowActiveTab);
      if (active) {
        base.workflow = active.name;
        base.workflowFile = `workflows/${active.id}.py`;
        // The canvas doc sibling (WorkflowGraphDoc) the panel edits.
        base.workflowGraphFile = `workflows/${active.id}.graph.json`;
      }
    }
    const extra = viewContextSnapshot(appView);
    return extra ? { ...base, ...extra } : base;
  }, [
    appView, sim.bundles, sim.activeGraphId, sim.bundle, openNode, openEdge,
    selectedNode, selectedEdge, replayActive, activeReplayKey, codeTabs,
    codeActiveTab, plotDashboards, plotActiveDashboard, workflowTabs, workflowActiveTab,
  ]);

  const openReplayPicker = useCallback(() => {
    setShowReplayPicker(true);
    if (!projectAdapter) return;
    setReplayPickerLoading(true);
    setReplayPickerError(null);
    Promise.all([
      projectAdapter.listDatasets(docId).catch(() => [] as DatasetRunInfo[]),
      projectAdapter.listReplays(docId).catch(() => [] as ReplayIndexEntry[]),
    ])
      .then(([runs, replays]) => {
        setReplayPickerRuns(runs);
        setReplayPickerReplays(replays);
      })
      .catch((e: unknown) =>
        setReplayPickerError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setReplayPickerLoading(false));
  }, [docId, projectAdapter]);

  const openReplayTab = useCallback((doc: ReplayDoc) => {
    const key = `replay-${++replayKeySeq.current}`;
    setReplayTabs((tabs) => [...tabs, { key, doc }]);
    setActiveReplayKey(key);
    setAppView('view');
  }, [setAppView]);

  const closeReplayTab = useCallback((key: string) => {
    setReplayTabs((tabs) => tabs.filter((t) => t.key !== key));
    setActiveReplayKey((cur) => (cur === key ? null : cur));
  }, []);

  const selectReplayTab = useCallback(
    (key: string) => {
      setActiveReplayKey(key);
      setAppView('view');
    },
    [setAppView],
  );

  // The prompt dialog fn — declared early: the replay-tab handlers below
  // (save/rename/record flows) close over it.
  const prompt = usePrompt();

  /** A tab's Save button landed: persist the reference doc + update the tab.
   *  Writing goes through the ordinary file API (PUT replays/<id>.json). */
  const saveReplayDoc = useCallback(
    async (key: string, doc: ReplayDoc) => {
      if (!projectAdapter) return;
      await projectAdapter.writeFile(docId, `replays/${doc.id}.json`, JSON.stringify(doc, null, 2));
      setReplayTabs((tabs) => tabs.map((t) => (t.key === key ? { ...t, doc } : t)));
    },
    [docId, projectAdapter],
  );

  /** The transport's Record button. DISARM is immediate; ARMING asks for a
   *  run name first (PromptDialog — Cancel/Record, default `Run {date time}`)
   *  — the name lands in the dataset registry's meta.label, which the replay
   *  picker and a run's replay tab display. */
  const handleRecord = useCallback(() => {
    if (!sim.setCapture) return;
    if (sim.captureState?.armed) {
      void sim.setCapture('disarm');
      return;
    }
    const def = defaultRunName();
    void (async () => {
      const label = await prompt({ title: 'Start recording', defaultValue: def, confirmLabel: 'Record' });
      if (label === null) return;
      await sim.setCapture?.('arm', { label: label.trim() || def });
    })();
  }, [prompt, sim.captureState?.armed, sim.setCapture]);

  /** Double-click a replay tab → rename (PromptDialog — Cancel/Save). A
   *  run-derived tab (doc.id === '') renames the RUN itself (the registry's
   *  meta.label through the label route — the picker + every future tab for
   *  that run follow); a saved period doc rewrites its reference file. Both
   *  update the tab's label optimistically. */
  const renameReplayTab = useCallback(
    async (key: string) => {
      const tab = replayTabs.find((t) => t.key === key);
      if (!tab) return;
      const label = await prompt({
        title: 'Rename replay',
        defaultValue: tab.doc.label,
        confirmLabel: 'Save',
      });
      const next = label === null ? '' : label.trim();
      if (!next || next === tab.doc.label) return;
      setReplayTabs((tabs) =>
        tabs.map((t) => (t.key === key ? { ...t, doc: { ...t.doc, label: next } } : t)),
      );
      if (tab.doc.id) {
        await saveReplayDoc(key, { ...tab.doc, label: next });
      } else if (projectAdapter) {
        try {
          await projectAdapter.renameDatasetRun(docId, tab.doc.runId, next);
        } catch {
          // The tab label already moved; the registry keeps its old name
          // (the picker shows it until a successful rename).
        }
      }
    },
    [docId, projectAdapter, prompt, replayTabs, saveReplayDoc],
  );

  /** The ACTIVE tab's session lands in a ref for the keyboard layer (plain
   *  assignment — no state, no re-render loop). */
  const registerActiveReplaySession = useCallback((s: ReplaySession | null) => {
    activeReplaySessionRef.current = s;
  }, []);
  // Deep-link file for the Code tab: set by other views' "open source" links;
  // opened once the CodePanel has mounted (visiting Code may mount it lazily —
  // the ref only populates after the lazy chunk resolves, so retry briefly).
  // The optional line is revealed + highlighted by the editor (the Workflows
  // tab's "Open code at step function").
  const [pendingCodeFile, setPendingCodeFile] = useState<{ path: string; line?: number } | null>(null);
  useEffect(() => {
    if (!pendingCodeFile) return;
    if (codePanelRef.current) {
      codePanelRef.current.openFile(pendingCodeFile.path, pendingCodeFile.line);
      setPendingCodeFile(null);
      return;
    }
    const started = Date.now();
    const t = window.setInterval(() => {
      if (codePanelRef.current) {
        codePanelRef.current.openFile(pendingCodeFile.path, pendingCodeFile.line);
        setPendingCodeFile(null);
      } else if (Date.now() - started > 10_000) {
        setPendingCodeFile(null); // panel never mounted — drop the deep link
      }
    }, 200);
    return () => window.clearInterval(t);
  }, [pendingCodeFile]);
  /** Open a project file AS A TAB in the Code editor (fetches + activates),
   * switching to the Code view first if needed. */
  const openInCodeTab = useCallback((path: string, line?: number) => {
    setAppView('code');
    setPendingCodeFile({ path, line });
  }, [setAppView]);
  const [showGrid, setShowGrid] = usePersistedState(storage, 'showGrid', false);
  const [showMinimap, setShowMinimap] = usePersistedState(storage, 'showMinimap', false);
  const [showSidebar, setShowSidebar] = usePersistedState(storage, 'showSidebar', true);
  // While a replay tab owns the canvas, the live-graph side surfaces (sidebar,
  // groups rail, tables dock) hide — their knobs/tables target the LIVE run.
  const viewSidebar = showSidebar && !replayActive;
  const viewGroups = showGroupsPanel && !replayActive;
  const viewTables = showTablesPanel && !replayActive;
  const [sidebarWidth, setSidebarWidth] = usePersistedState(storage, 'sidebarWidth', 340);
  const [groupsWidth, setGroupsWidth] = usePersistedState(storage, 'groupsWidth', 220);
  const [tablesHeight, setTablesHeight] = usePersistedState(storage, 'tablesHeight', 300);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Table → flow-net graph dialog (opened from the docked tables panel's
  // toolbar; the Data tab opens its own instance).
  const [showFlows, setShowFlows] = useState(false);
  const [showAgentsPanel, setShowAgentsPanel] = useState(false);
  const [showTriggersPanel, setShowTriggersPanel] = useState(false);
  // Services modal (running docker containers) — opened from the MenuBar's
  // right slot + the StatusBar's docker button.
  const [showServices, setShowServices] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  // The automatic version checker (frontend drift vs the served manifest +
  // stack drift vs the live backend digests — see src/version/).
  const versionCheck = useVersionCheck();
  // The close-project "stop services?" dialog (File → Close).
  const [showStopDialog, setShowStopDialog] = useState(false);
  // Per-project "stop services on close" (Settings → Engine). null = never
  // chosen — the close flow asks; true/false = remembered choice.
  const [stopServicesOnClose, setStopServicesOnClose] = usePersistedState<boolean | null>(
    storage, `stopServicesOnClose.${docId}`, null);
  // Workflow worker autostart (Settings → Engine) — lives in the backend
  // project manifest, not localStorage; hydrated per open project. undefined
  // = not loaded (or no backend project) → the toggle row hides.
  const [workflowsAutostart, setWorkflowsAutostart] = useState<boolean | undefined>(undefined);
  const [showHelp, setShowHelp] = useState(false);
  // The agent decisions log modal — opened from the StatusBar's decisions
  // button (the logs UX: modal, not a sidebar section).
  const [showDecisions, setShowDecisions] = useState(false);
  // The matrix codegen modal — opened from the StatusBar's code button
  // (graph → incidence/adjacency/Laplacian → NumPy, for LP setups).
  const [showMatrixCode, setShowMatrixCode] = useState(false);
  // The transport Stop flow is in flight (stop model → task poll → the
  // "Stop other running tasks?" modal). Keeps the MenuBar's Stop button a
  // spinner until the user answers the modal (and any follow-up stops land).
  const [stoppingModel, setStoppingModel] = useState(false);
  // Download / Export format pickers (File menu). Each opens a focused modal
  // listing the available formats; selecting one fires the matching action.
  const [downloadPickerOpen, setDownloadPickerOpen] = useState(false);
  const [exportPickerOpen, setExportPickerOpen] = useState(false);
  // The hidden file input behind Menu → Open… AND the Ctrl+O binding. Lives
  // here (not inside MenuBar) so the key binding can click it; MenuBar takes
  // it as a prop.
  const openFileInputRef = useRef<HTMLInputElement | null>(null);

  // #12 — apply the theme to <html data-theme>.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const confirm = useConfirm();

  // ─── Context-menu builders (#3) ─────────────────────────────────────────
  // These callbacks must be created unconditionally (before any early return)
  // so the hook count stays stable across renders. They close over `sim.bundle`
  // and only fire on user action, by which time the bundle is loaded.
  const openNodeMenu = useCallback(
    (id: string, pos: { x: number; y: number }) => {
      if (!sim.bundle) return;
      const entries = [
        {
          label: 'Edit',
          icon: <PencilSimple size={14} />,
          onSelect: () => { setOpenNode(id); setSelectedNode(id); setSelectedEdge(null); setOpenEdge(null); },
        },
        {
          label: 'Duplicate',
          icon: <Copy size={14} />,
          onSelect: () => sim.duplicateNode?.(id),
        },
        {
          label: 'Agent',
          icon: <Plugs size={14} />,
          onSelect: () => { setOpenAgentNode(id); setMenu(null); },
        },
        {
          label: 'Delete',
          icon: <Trash size={14} />,
          danger: true,
          onSelect: async () => {
            const lbl = sim.bundle!.graph.nodes.find((n) => n.id === id)?.label ?? id;
            const ok = await confirm({
              title: 'Delete node?',
              body: <>This removes <strong>{lbl}</strong> and every edge connected to it. This cannot be undone.</>,
              danger: true,
              confirmLabel: 'Delete node',
            });
            if (ok) {
              sim.removeNode?.(id);
              setSelectedNode(null);
              setOpenNode(null);
            }
          },
        },
      ];
      setMenu({ x: pos.x, y: pos.y, entries });
    },
    [confirm, sim],
  );

  const openEdgeMenu = useCallback(
    (id: string, pos: { x: number; y: number }) => {
      if (!sim.bundle) return;
      const e = sim.bundle.graph.edges.find((x) => x.id === id);
      const entries = [
        {
          label: 'Edge Settings',
          icon: <PencilSimple size={14} />,
          onSelect: () => { setOpenEdge(id); setEdgeModalTab('settings'); setSelectedEdge(id); setSelectedNode(null); setOpenNode(null); },
        },
        {
          label: 'Edge Params',
          icon: <TagSimple size={14} />,
          onSelect: () => { setOpenEdge(id); setEdgeModalTab('params'); setSelectedEdge(id); setSelectedNode(null); setOpenNode(null); },
        },
        {
          label: 'Reverse direction',
          icon: <ArrowsLeftRight size={14} />,
          onSelect: () => {
            if (!e) return;
            sim.updateEdge?.({ ...e, source: e.target, target: e.source });
          },
        },
        {
          label: 'Agent',
          icon: <Plugs size={14} />,
          onSelect: () => { setOpenAgentEdge(id); setMenu(null); },
        },
        {
          label: 'Delete',
          icon: <Trash size={14} />,
          danger: true,
          onSelect: async () => {
            const ok = await confirm({
              title: 'Delete edge?',
              body: <>This removes the connection <code>{e?.id}</code>. This cannot be undone.</>,
              danger: true,
              confirmLabel: 'Delete edge',
            });
            if (ok) {
              sim.removeEdge?.(id);
              setSelectedEdge(null);
              setOpenEdge(null);
            }
          },
        },
      ];
      setMenu({ x: pos.x, y: pos.y, entries });
    },
    [confirm, sim],
  );

  const openPaneMenu = useCallback(
    (canvasPos: { x: number; y: number }, clientPos: { x: number; y: number }) => {
      const entries = [
        {
          label: 'Add node here',
          icon: <Plus size={14} />,
          onSelect: () => {
            const id = `node_${Date.now().toString(36)}`;
            sim.addNode?.({
              id,
              label: 'New node',
              icon: 'Gear',
              kind: 'actor',
              params: [],
              position: canvasPos,
            });
            // Select (highlight + sidebar) but do NOT auto-open the modal —
            // the user double-clicks or uses Edit to open it.
            setSelectedNode(id);
            setSelectedEdge(null);
            setOpenNode(null);
          },
        },
        {
          label: 'Add group here',
          icon: <Plus size={14} />,
          onSelect: () => {
            // Default-size rect centred on the cursor. Membership is derived
            // spatially, so a node is in this group iff its centre is inside
            // the rect — dragging nodes in/out updates it live.
            const id = `group_${Date.now().toString(36)}`;
            const W = 320;
            const H = 220;
            sim.addNodeGroup?.({
              id,
              label: 'New group',
              rect: { x: canvasPos.x - W / 2, y: canvasPos.y - H / 2, w: W, h: H },
            });
            // Select the new group (highlight + sidebar row) without opening
            // its modal — the user opens it via double-click / Edit.
            setSelectedGroup(id);
            setSelectedNode(null);
            setOpenGroup(null);
          },
        },
      ];
      setMenu({ x: clientPos.x, y: clientPos.y, entries });
    },
    [sim],
  );

  // Group context menu (right-click a group rect): edit, toggle background
  // fill, delete. The "Background" entry mirrors the group's showBackground
  // flag — checked = filled, unchecked (default) = outline only.
  const openGroupMenu = useCallback(
    (id: string, pos: { x: number; y: number }) => {
      const g = sim.bundle?.nodeGroups?.find((q) => q.id === id);
      const entries = [
        {
          label: 'Edit',
          icon: <PencilSimple size={14} />,
          onSelect: () => { setOpenGroup(id); setSelectedGroup(id); setSelectedNode(null); setSelectedEdge(null); setOpenNode(null); setOpenEdge(null); },
        },
        {
          label: 'Background',
          checked: g?.showBackground === true,
          onSelect: () => {
            if (!g) return;
            sim.updateNodeGroup?.({ ...g, showBackground: !g.showBackground });
          },
        },
        {
          label: 'Delete',
          icon: <Trash size={14} />,
          danger: true,
          onSelect: async () => {
            const lbl = sim.bundle?.nodeGroups?.find((g) => g.id === id)?.label ?? id;
            const ok = await confirm({
              title: 'Delete group?',
              body: <>This removes the group <strong>{lbl}</strong>. Member nodes are not deleted.</>,
              danger: true,
              confirmLabel: 'Delete group',
            });
            if (ok) {
              sim.removeNodeGroup?.(id);
              setSelectedGroup(null);
              setOpenGroup(null);
            }
          },
        },
      ];
      setMenu({ x: pos.x, y: pos.y, entries });
    },
    [confirm, sim],
  );

  // Bend context menu: change the bend's mode (sharp/smooth/default) or delete.
  const openBendMenu = useCallback(
    (edgeId: string, idx: number, clientPos: { x: number; y: number }) => {
      const e = sim.bundle?.graph.edges.find((q) => q.id === edgeId);
      const bend = e?.bends?.[idx];
      // 'default' is a legacy alias for 'sharp' — collapse it so the menu's
      // "current" highlight is accurate for old bend data.
      const cur: 'sharp' | 'smooth' = bend?.mode === 'smooth' ? 'smooth' : 'sharp';
      const setMode = (mode: 'sharp' | 'smooth') => {
        if (!e?.bends) return;
        sim.setBends?.(edgeId, e.bends.map((b, i) => (i === idx ? { ...b, mode } : b)));
      };
      const del = () => {
        if (!e?.bends) return;
        sim.setBends?.(edgeId, e.bends.filter((_, i) => i !== idx));
      };
      const entries = [
        { label: 'Sharp', onSelect: () => setMode('sharp'), disabled: cur === 'sharp' },
        { label: 'Smooth', onSelect: () => setMode('smooth'), disabled: cur === 'smooth' },
        { label: 'Delete point', danger: true, icon: <Trash size={14} />, onSelect: del },
      ];
      setMenu({ x: clientPos.x, y: clientPos.y, entries });
    },
    [sim],
  );

  // ── Multi-graph handlers (graph-tab strip) ──────────────────────────────
  // The graph list + active id come from the sim snapshot. Create/rename go
  // through PromptDialog; delete through ConfirmDialog (house rule: no
  // window.confirm). Switching is a local call on the adapter (per-client UI).
  // These useCallbacks MUST be before the early returns below so the hook count
  // stays stable across renders.
  const handleSelectGraph = useCallback(
    (id: string) => {
      // Clicking a graph tab leaves replay mode — the live canvas owns the
      // surface again (the replay tabs stay open in the strip).
      setActiveReplayKey(null);
      sim.setActiveGraph?.(id);
      setSavedActiveGraph(id); // persist so a reload reopens this graph
    },
    [sim, setSavedActiveGraph],
  );
  const handleCreateGraph = useCallback(async () => {
    const name = await prompt({ title: 'New graph', defaultValue: 'New Graph' });
    if (!name) return;
    sim.addGraph?.(name);
  }, [prompt, sim]);
  const handleRenameGraph = useCallback(async (id: string) => {
    const cur = (sim.bundles ?? []).find((b) => b.id === id);
    const name = await prompt({ title: 'Rename graph', defaultValue: cur?.label ?? id });
    if (!name) return;
    sim.renameGraph?.(id, name);
  }, [prompt, sim]);
  const handleDeleteGraph = useCallback(async (id: string) => {
    if (id === 'main') return; // Main is non-removable (defensive; UI hides it too)
    const cur = (sim.bundles ?? []).find((b) => b.id === id);
    const ok = await confirm({
      title: 'Delete graph',
      body: `Delete "${cur?.label ?? id}"? This removes the graph and its file. This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    sim.removeGraph?.(id);
  }, [confirm, sim]);

  // ── Chat + AskBox (backend-gated LLM surfaces) ──────────────────────────
  // These hooks MUST run before the early returns below (Rules of Hooks: the
  // hook count is fixed across renders regardless of sim.error / sim.bundle).
  // canRemote is derived from sim.mode (always available); backendOrigin from
  // auth.wsUrl (always available). The AI config fetch + the Cmd/Ctrl+K handler
  // are backend-gated but unconditional at the hook level.
  const canRemote = sim.mode === 'api' || sim.mode === 'relay';
  const [showChat, setShowChat] = useState(false);
  const [showAsk, setShowAsk] = useState(false);
  const [aiProvider, setAiProvider] = usePersistedState<string | null>(storage, 'ai.provider', null);
  const [aiModel, setAiModel] = usePersistedState<string | null>(storage, 'ai.model', null);
  const backendOrigin = auth.wsUrl ? httpOriginOf(auth.wsUrl, '') : null;
  const { config: aiConfig, reload: reloadAi } = useAiConfig(canRemote ? backendOrigin : null, auth.token);
  const aiChoice = resolveChoice(aiConfig, aiProvider, aiModel);

  // Hydrate the workflow worker autostart preference from the project
  // manifest when a backend project is open (runs before the early returns —
  // Rules of Hooks). Unload/hide the toggle otherwise.
  useEffect(() => {
    let cancelled = false;
    setWorkflowsAutostart(undefined);
    if (!(canRemote && docId && libraryAdapter instanceof RemoteProjectAdapter)) return;
    libraryAdapter.getWorkflowSettings(docId)
      .then((s) => { if (!cancelled) setWorkflowsAutostart(s.enabled ? s.autostart : undefined); })
      .catch(() => { /* leave hidden — workflows not set up */ });
    return () => { cancelled = true; };
  }, [canRemote, docId, libraryAdapter]);

  // Best-effort stop of this project's docker services (the remembered
  // "stop on close" choice + the close dialog both funnel through here).
  const stopProjectServices = useCallback(() => {
    if (!backendOrigin || !docId) return;
    void fetch(`${backendOrigin}/api/projects/${encodeURIComponent(docId)}/services/stop`, {
      method: 'POST',
      headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : undefined,
    }).catch(() => { /* fire-and-forget */ });
  }, [backendOrigin, docId, auth.token]);

  // ─── File ops (shared by the Menu entries + the Ctrl+S/⌘S key bindings) ──
  const saveDoc = useCallback(async () => {
    const b = sim.exportBundle?.();
    if (!b) return;
    // Persist the open document (local applies immediately; remote PUTs).
    await documentAdapter.save(docId, b);
    const entries = await libraryAdapter.list();
    const known = entries.find((e) => e.id === b.id);
    if (known) {
      await libraryAdapter.save(b);
    } else {
      const name = await prompt({ title: 'Save to library', defaultValue: b.label || b.id });
      if (!name) return;
      const entry = await libraryAdapter.save({ ...b, label: name }, { slug: name });
      sim.replaceBundle?.({ ...b, id: entry.id, label: name });
      if (auth.wsUrl) {
        storage.saveBundle({ ...b, id: entry.id, label: name });
        navigate(`/doc/${entry.id}/edit`);
      }
    }
  }, [sim, documentAdapter, docId, libraryAdapter, prompt, auth.wsUrl, storage, navigate]);

  const saveDocAs = useCallback(async () => {
    const b = sim.exportBundle?.();
    if (!b) return;
    const name = await prompt({ title: 'Save As', defaultValue: `${b.label || b.id} copy` });
    if (!name) return;
    // Save As of a backend project must copy the FULL package (graph +
    // manifest + src/ + data/) so the copy keeps its model, tables, and
    // code — a graph-only importBundle would drop all of that. Use
    // duplicate() (download source ZIP → re-upload under a fresh uuid)
    // when the adapter supports it; fall back to importBundle otherwise.
    const sourceId = docId;
    const newId = b.id === sourceId ? uuid() : b.id;
    if (auth.wsUrl && (libraryAdapter as RemoteProjectAdapter).duplicate) {
      try {
        const entry = await (libraryAdapter as RemoteProjectAdapter).duplicate(sourceId, {
          id: newId,
          label: name,
        });
        sim.replaceBundle?.({ ...b, id: entry.id, label: name });
        storage.saveBundle({ ...b, id: entry.id, label: name });
        navigate(`/doc/${entry.id}/edit`);
        return;
      } catch {
        // duplicate failed (source has no downloadable package, etc.) —
        // fall through to the graph-only importBundle below.
      }
    }
    const entry = await libraryAdapter.importBundle({ ...b, label: name }, { slug: newId });
    sim.replaceBundle?.({ ...b, id: entry.id, label: name });
    if (auth.wsUrl) {
      storage.saveBundle({ ...b, id: entry.id, label: name });
      navigate(`/doc/${entry.id}/edit`);
    }
  }, [sim, docId, auth.wsUrl, libraryAdapter, prompt, storage, navigate]);

  // ─── Graph-selection actions (context menus AND the keyboard bindings) ───
  // Selection = React Flow's multi-select (shift-click / marquee) UNION the
  // App's single selection (which drives highlight + sidebar).
  const selectionIds = useCallback((): { nodeIds: Set<string>; edgeIds: Set<string> } => {
    const nodeIds = new Set(canvasRef.current?.getSelectedNodeIds() ?? []);
    if (selectedNode) nodeIds.add(selectedNode);
    const edgeIds = new Set(canvasRef.current?.getSelectedEdgeIds() ?? []);
    if (selectedEdge) edgeIds.add(selectedEdge);
    return { nodeIds, edgeIds };
  }, [selectedNode, selectedEdge]);

  // Keyboard Delete removes immediately — undo (Ctrl+Z) is the safety net.
  // The context-menu Delete keeps its ConfirmDialog.
  const deleteSelection = useCallback(() => {
    const { nodeIds, edgeIds } = selectionIds();
    if (nodeIds.size === 0 && edgeIds.size === 0) return;
    for (const id of edgeIds) sim.removeEdge?.(id);
    for (const id of nodeIds) sim.removeNode?.(id); // cascades connected edges
    setSelectedNode(null);
    setSelectedEdge(null);
    setOpenNode(null);
    setOpenEdge(null);
    canvasRef.current?.clearSelection();
  }, [selectionIds, sim]);

  const duplicateSelection = useCallback(() => {
    for (const id of selectionIds().nodeIds) sim.duplicateNode?.(id);
  }, [selectionIds, sim]);

  const copySelection = useCallback(
    (cut: boolean) => {
      const g = sim.bundle?.graph;
      const { nodeIds, edgeIds } = selectionIds();
      if (!g || nodeIds.size === 0) return; // edges alone can't paste (no endpoints)
      clipboardRef.current = captureClipboard(
        g.nodes.filter((n) => nodeIds.has(n.id)),
        g.edges.filter((e) => edgeIds.has(e.id)),
      );
      pasteCountRef.current = 0;
      if (cut) deleteSelection();
    },
    [sim, selectionIds, deleteSelection],
  );

  const pasteFromClipboard = useCallback(() => {
    const clip = clipboardRef.current;
    const g = sim.bundle?.graph;
    if (!clip || clip.nodes.length === 0 || !g) return;
    const occupied = new Set<string>([
      ...g.nodes.map((n) => n.id),
      ...g.edges.map((e) => e.id),
    ]);
    const batch = pasteClipboard(clip, occupied, pasteCountRef.current);
    pasteCountRef.current += 1;
    for (const n of batch.nodes) sim.addNode?.(n);
    for (const e of batch.edges) sim.addEdge?.(e);
    // Highlight the head of the pasted batch (single-select model).
    const head = batch.nodes[0];
    if (head) {
      setSelectedNode(head.id);
      setSelectedEdge(null);
    }
  }, [sim]);

  // Enter opens the selection's modal — same semantics as double-click.
  const openSelected = useCallback(() => {
    const { nodeIds, edgeIds } = selectionIds();
    const nodeId = selectedNode ?? [...nodeIds][0];
    if (nodeId) {
      setOpenNode(nodeId);
      setSelectedNode(nodeId);
      setSelectedEdge(null);
      setOpenEdge(null);
      return;
    }
    const edgeId = selectedEdge ?? [...edgeIds][0];
    if (edgeId) {
      setOpenEdge(edgeId);
      setEdgeModalTab('params');
      setSelectedEdge(edgeId);
      setSelectedNode(null);
      setOpenNode(null);
    }
  }, [selectionIds, selectedNode, selectedEdge]);

  const clearCanvasSelection = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
    setSelectedGroup(null);
    canvasRef.current?.clearSelection();
  }, []);

  // ─── The global key layer ─────────────────────────────────────────────────
  // One window listener for every app-level binding (see useGlobalKeys — the
  // single home for global shortcuts; don't add scattered keydown effects).
  // Graph keys: View tab + an editable session. File keys: editable session,
  // any tab EXCEPT Code (Monaco owns Ctrl+S there). The toggles are
  // presence-gated (canRemote) since they need the backend. Replay keys
  // (←/→/Shift+←/→/Space) take over while a replay tab is active — graph
  // bindings sleep (they'd edit the live bundle under a frozen canvas).
  const replayKeysEnabled = replayActive && canRemote;
  // Workflow keys: the SAME standard bindings, retargeted at the Workflows
  // tab's canvas through the panel handle (Delete/Esc/Ctrl+A/Enter/fit/zoom/
  // undo). Copy/paste/duplicate have no workflow equivalent — View-only.
  const wfKeysEnabled = appView === 'workflows' && canRemote;
  const graphKeysEnabled = appView === 'view' && sim.canEdit && !replayActive;
  const fileKeysEnabled = sim.canEdit && appView !== 'code';
  useGlobalKeys(
    {
      toggleAsk: canRemote ? () => setShowAsk((v) => !v) : undefined,
      toggleChat: canRemote ? () => setShowChat((v) => !v) : undefined,
      openLibrary: () => setShowLibraryModal(true),
      save: fileKeysEnabled ? () => void saveDoc() : undefined,
      saveAs: fileKeysEnabled ? () => void saveDocAs() : undefined,
      openFile: fileKeysEnabled ? () => openFileInputRef.current?.click() : undefined,
      undo: wfKeysEnabled
        ? () => workflowPanelRef.current?.keyUndo()
        : graphKeysEnabled ? () => sim.undo?.() : undefined,
      redo: wfKeysEnabled
        ? () => workflowPanelRef.current?.keyRedo()
        : graphKeysEnabled ? () => sim.redo?.() : undefined,
      deleteSelection: wfKeysEnabled
        ? () => workflowPanelRef.current?.keyDelete()
        : graphKeysEnabled ? deleteSelection : undefined,
      duplicateSelection: graphKeysEnabled ? duplicateSelection : undefined,
      copySelection: graphKeysEnabled ? () => copySelection(false) : undefined,
      cutSelection: graphKeysEnabled ? () => copySelection(true) : undefined,
      paste: graphKeysEnabled ? pasteFromClipboard : undefined,
      selectAll: wfKeysEnabled
        ? () => workflowPanelRef.current?.keySelectAll()
        : graphKeysEnabled ? () => canvasRef.current?.selectAll() : undefined,
      openSelected: wfKeysEnabled
        ? () => workflowPanelRef.current?.keyOpenSelected()
        : graphKeysEnabled ? openSelected : undefined,
      clearSelection: wfKeysEnabled
        ? () => workflowPanelRef.current?.keyClearSelection()
        : graphKeysEnabled ? clearCanvasSelection : undefined,
      fitView: appView === 'view' ? () => canvasRef.current?.fitView()
        : wfKeysEnabled ? () => workflowPanelRef.current?.keyFitView() : undefined,
      zoomIn: appView === 'view' ? () => canvasRef.current?.zoomIn()
        : wfKeysEnabled ? () => workflowPanelRef.current?.keyZoomIn() : undefined,
      zoomOut: appView === 'view' ? () => canvasRef.current?.zoomOut()
        : wfKeysEnabled ? () => workflowPanelRef.current?.keyZoomOut() : undefined,
      replayStep: replayKeysEnabled
        ? (delta: number) => activeReplaySessionRef.current?.move(delta)
        : undefined,
      replayTogglePlay: replayKeysEnabled
        ? () => {
            const s = activeReplaySessionRef.current;
            if (!s) return;
            s.playing ? s.pause() : s.play();
          }
        : undefined,
    },
    {
      // Non-modal overlays (modals/confirm/prompt are caught via aria-modal
      // inside the hook; the context menu + format pickers are not modals).
      overlayOpen: !!menu || downloadPickerOpen || exportPickerOpen || showReplayPicker,
      graphKeys: graphKeysEnabled || wfKeysEnabled,
      fileKeys: fileKeysEnabled,
      replayKeys: replayKeysEnabled,
    },
  );

  // NOTE: no full-screen early return on sim.error — errors (load failures,
  // runtime tick rejections) render inside the graph canvas so the rest of
  // the UI stays usable. Load failures seed an empty Main bundle in useSim,
  // so `!sim.bundle` is only true while genuinely loading.
  if (!sim.bundle || !sim.state) {
    return (
      <div className="app-shell">
        <div className="loading">Loading…</div>
      </div>
    );
  }

  const node = openNode ? sim.bundle.graph.nodes.find((n) => n.id === openNode) : null;
  const edge = openEdge ? sim.bundle.graph.edges.find((e) => e.id === openEdge) : null;
  const edgeSource = edge ? sim.bundle.graph.nodes.find((n) => n.id === edge.source) : null;
  const edgeTarget = edge ? sim.bundle.graph.nodes.find((n) => n.id === edge.target) : null;
  const group = openGroup
    ? sim.bundle.nodeGroups?.find((g) => g.id === openGroup) ?? null
    : null;
  const nodeLabel = (id: string): string =>
    sim.bundle!.graph.nodes.find((n) => n.id === id)?.label ?? id;
  /** Agent identity key → display name (bundle-aware; the activity modal's
   *  sidebar labels). `null`-agent entries render as "Model" in the modal. */
  const agentName = (id: string): string => agentDisplayName(id, sim.bundle);

  // Format-picker option lists. Built from the current sim mode so the picker
  // disables (with a reason) formats that aren't available in this session.
  const downloadOptions: FormatOption[] = [
    {
      id: 'project-zip',
      label: 'Project (ZIP)',
      description: 'graph + manifest + code',
      disabled: !canRemote,
      disabledReason: 'Connect to a backend to download the full project.',
    },
    {
      id: 'bundle-json',
      label: 'Bundle (JSON)',
      description: 'the graph definition only',
    },
  ];
  const exportOptions: FormatOption[] = [
    {
      id: 'csv-wide',
      label: 'CSV (wide)',
      description: 'one row per step, every stock/flow as a column',
      disabled: !canRemote,
      disabledReason: 'Export needs a connected backend with a captured run.',
    },
    {
      id: 'csv-long',
      label: 'CSV (long)',
      description: 'one row per (step, node, field) — tidy format',
      disabled: !canRemote,
      disabledReason: 'Export needs a connected backend with a captured run.',
    },
    {
      id: 'trajectory',
      label: 'Trajectory (JSONL)',
      description: 'one JSON object per step — agent training data',
      disabled: !canRemote,
      disabledReason: 'Export needs a connected backend with a captured run.',
    },
  ];

  // Graph tabs derived from sim snapshot (non-hook; safe after early returns).
  const graphTabs = (sim.bundles ?? []).map((b) => ({ id: b.id, name: b.label }));

  return (
    <ReactFlowProvider>
    <div className="app-shell">
      <MenuBar
        // File ops only exist on editable adapters (LocalAdapter). Gate on
        // canEdit so the read-only demo mode hides them.
        canOpen={sim.canEdit}
        canSave={sim.canEdit}
        onLibrary={() => setShowLibraryModal(true)}
        onOpenChat={canRemote ? () => setShowChat(true) : undefined}
        onOpenAsk={canRemote ? () => setShowAsk(true) : undefined}
        onSave={() => void saveDoc()}
        onSaveAs={() => void saveDocAs()}
        // The hidden Open… input lives at the App so Ctrl+O can click it too.
        openInputRef={openFileInputRef}
        // Undo/Redo entries mirror Ctrl+Z / Ctrl+Shift+Z — the workflow
        // editor's own history while the Workflows tab is active, the View
        // graph's edit history otherwise.
        canUndo={wfKeysEnabled ? wfHistory.canUndo : sim.canUndo}
        canRedo={wfKeysEnabled ? wfHistory.canRedo : sim.canRedo}
        onUndo={wfKeysEnabled
          ? () => workflowPanelRef.current?.keyUndo()
          : () => sim.undo?.()}
        onRedo={wfKeysEnabled
          ? () => workflowPanelRef.current?.keyRedo()
          : () => sim.redo?.()}
        // Download / Export now open format pickers (File → Download…/Export…)
        // so the user picks the format from a focused list instead of a flat
        // dump of menu entries.
        onDownloadPicker={() => setDownloadPickerOpen(true)}
        canDownloadProject={sim.mode === 'api' || sim.mode === 'relay'}
        onExportPicker={() => setExportPickerOpen(true)}
        canExportRun={sim.mode === 'api' || sim.mode === 'relay'}
        onOpen={async (file) => {
          try {
            const text = await file.text();
            sim.replaceBundle?.(JSON.parse(text));
          } catch {
            // Malformed JSON / wrong shape — leave the bundle unchanged.
          }
        }}
        onClose={() => {
          // Close asks about the project's docker services first (unless a
          // remembered choice exists) — see the StopServicesDialog below.
          if (canRemote && backendOrigin && docId) {
            if (stopServicesOnClose === null) {
              setShowStopDialog(true);
              return;
            }
            if (stopServicesOnClose) void stopProjectServices();
          }
          sim.reset();
          navigate('/');
        }}
        onOpenServices={canRemote ? () => setShowServices(true) : undefined}
        // The automatic version checker's menu entry (Update modal).
        updateAvailable={versionCheck.frontendUpdate === true || versionCheck.stackUpdate === true}
        onOpenUpdate={() => setShowUpdate(true)}
        // Settings — Reset here returns the sim to step 0 but stays in the
        // studio. Close (Menu) is reset + exit. Settings/tick-rate/connection
        // now live in the SettingsModal (opened via onOpenSettings).
        canReset={sim.state.step !== 0}
        onReset={() => sim.reset()}
        onOpenSettings={() => setShowSettings(true)}
        // About
        onHelp={() => setShowHelp(true)}
        version={appVersion}
        // Transport / status. Whenever a backend project is open (api OR
        // relay), Play/Stop drive the backend model subprocess keyed on the
        // project id — POST /run/start + /run/stop — and `running` reflects
        // the subprocess state (polled + set from the POST responses) so the
        // buttons toggle correctly even across reconnects/crashes. The old
        // api-mode behaviour (Play spun the WS demo replay pump) is gone —
        // replays are dead. Only local in-browser mode (no backend) falls
        // back to the LocalAdapter tick loop.
        status={sim.status}
        running={
          (sim.mode === 'api' || sim.mode === 'relay')
            ? (sim.runStatus?.running ?? false)
            : sim.status === 'running'
        }
        onStart={
          (sim.mode === 'api' || sim.mode === 'relay')
            ? () => void sim.runStart?.()
            : sim.start
        }
        onStop={
          (sim.mode === 'api' || sim.mode === 'relay')
            // Stopping the model must not orphan the project's other tasks
            // (docker execution: the workflow worker / trainers / notebook
            // keep running). The shared flow asks once whether to stop them.
            // stoppingModel keeps the transport on a spinner until the flow
            // (incl. the confirm modal) finishes.
            ? () => {
                setStoppingModel(true);
                void stopModelAndPromptOthers({
                  adapter: libraryAdapter as RemoteProjectAdapter,
                  projectId: docId,
                  stopModel: async () => { await sim.runStop?.(); },
                  confirm,
                  modelWasRunning: sim.runStatus?.running ?? false,
                }).finally(() => setStoppingModel(false));
              }
            : sim.stop
        }
        stopping={stoppingModel}
        // The Record button (transport's leftmost): arms/disarms the live
        // recording — ring buffer + durable dataset run (manifest
        // record.saveDataset, default true — docs/TRAINING.md §4). Only when
        // a backend recording surface is wired (captureState is null in
        // local/edit modes → disabled). Arming asks for a run name first
        // (handleRecord — the name rides meta.label into the registry).
        recording={sim.captureState?.armed ?? false}
        onToggleRecord={sim.setCapture ? handleRecord : undefined}
        // Agents kill-switch (transport): shown only for backend projects
        // with agents configured. `agentsEnabled` rides the run-status poll;
        // absent on older backends defaults to enabled.
        agentsEnabled={
          canRemote && hasAgents ? (sim.runStatus?.agentsEnabled ?? true) : undefined
        }
        onToggleAgents={
          canRemote && hasAgents
            ? () => { void sim.toggleAgents?.(!(sim.runStatus?.agentsEnabled ?? true)); }
            : undefined
        }
        relay={sim.mode === 'relay'}
        theme={theme}
        onToggleTheme={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
        // Hide the global transport while the Code or Workflows tab is active -
        // those surfaces own their own run controls (the Tasks panel / the
        // workflow run bar).
        hideTransport={appView === 'code' || appView === 'workflows'}
        showViewTabs={canRemote}
        view={appView}
        onView={setAppView}
        // File tabs rendered inline in the MenuBar (after the switcher) when
        // in Code view. Driven through the imperative handle.
        codeTabs={appView === 'code' && canRemote ? codeTabs : []}
        codeActiveTab={codeActiveTab}
        onSelectCodeTab={(path) => codePanelRef.current?.selectTab(path)}
        onCloseCodeTab={(path) => codePanelRef.current?.closeTab(path)}
        onSaveCodeTab={(path) => void codePanelRef.current?.saveTab(path)}
        onRenameCodeTab={(path) => codePanelRef.current?.renameTab(path)}
        onCreateCodeEntry={(sql) => codePanelRef.current?.createEntry(sql)}
        // Save button: shown in the MenuBar only when the Code tab is active AND
        // there are dirty files. The count rides on the button as a badge.
        codeDirtyCount={appView === 'code' && canRemote ? codeDirtyCount : 0}
        onCodeSave={() => void codePanelRef.current?.saveAll()}
        // Plot-tab dashboard tabs + rail toggle (inline like code file-tabs).
        plotDashboards={appView === 'plot' && canRemote ? plotDashboards : []}
        plotActiveDashboard={plotActiveDashboard}
        onSelectDashboard={(id) => plotPanelRef.current?.selectDashboard(id)}
        onCreateDashboard={() => plotPanelRef.current?.createDashboard()}
        onRenameDashboard={(id) => plotPanelRef.current?.renameDashboard(id)}
        onDeleteDashboard={(id) => plotPanelRef.current?.deleteDashboard(id)}
        // Graph tabs (multi-graph) — pinned, always visible in View mode.
        graphs={appView === 'view' ? graphTabs : []}
        activeGraphId={sim.activeGraphId}
        onSelectGraph={handleSelectGraph}
        onCreateGraph={handleCreateGraph}
        onRenameGraph={handleRenameGraph}
        onDeleteGraph={handleDeleteGraph}
        // Replay tabs — transient recorded periods in the same strip.
        replayTabs={
          appView === 'view' && canRemote
            ? replayTabs.map((t) => ({ key: t.key, label: t.doc.label, runId: t.doc.runId }))
            : []
        }
        activeReplayKey={activeReplayKey}
        onSelectReplayTab={selectReplayTab}
        onCloseReplayTab={closeReplayTab}
        onRenameReplayTab={canRemote ? renameReplayTab : undefined}
        onOpenReplayPicker={canRemote ? openReplayPicker : undefined}
        // Workflows-tab tabs - open documents, driven through the panel handle.
        workflowTabs={appView === 'workflows' && canRemote ? workflowTabs : []}
        workflowActiveTab={workflowActiveTab}
        onSelectWorkflowTab={(id) => workflowPanelRef.current?.selectWorkflow(id)}
        onCloseWorkflowTab={(id) => workflowPanelRef.current?.closeWorkflow(id)}
        onAddWorkflowTab={() => workflowPanelRef.current?.openPicker()}
        showLeftSidebar={
          appView === 'view' ? showSidebar :
          appView === 'data' ? showDataSidebar :
          appView === 'plot' ? showPlotRail :
          appView === 'code' ? showCodeSidebar :
          appView === 'workflows' ? showWorkflowRail : false
        }
        onToggleLeftSidebar={
          appView === 'view' ? () => setShowSidebar((v) => !v) :
          appView === 'data' ? () => setShowDataSidebar((v) => !v) :
          appView === 'plot' ? () => setShowPlotRail((v) => !v) :
          appView === 'code' ? () => setShowCodeSidebar((v) => !v) :
          appView === 'workflows' ? () => setShowWorkflowRail((v) => !v) : undefined
        }
      />
      <div className="plot-shell" style={{ display: appView === 'plot' && canRemote ? 'block' : 'none' }}>
        {visitedPlot && canRemote && (
          <Suspense fallback={<div className="code-panel-msg"><span>Loading Plot tab…</span></div>}>
            <ErrorBoundary label="The Plot tab">
            <PlotPanel
              ref={plotPanelRef}
              projectId={docId}
              projectAdapter={libraryAdapter as RemoteProjectAdapter}
              storage={storage}
              bundle={sim.bundle}
              bundles={sim.bundles}
              recentTicks={sim.recentTicks}
              theme={theme}
              showRail={showPlotRail}
              railWidth={plotRailWidth}
              setRailWidth={setPlotRailWidth}
              onDashboardsChange={setPlotDashboards}
              onActiveDashboardChange={setPlotActiveDashboard}
            />
            </ErrorBoundary>
          </Suspense>
        )}
      </div>

      <div className="data-shell" style={{ display: appView === 'data' && canRemote ? 'block' : 'none' }}>
        {visitedData && canRemote && (
          <Suspense fallback={<div className="code-panel-msg"><span>Loading Data tab…</span></div>}>
            <ErrorBoundary label="The Data tab">
            <DataPanel
              projectId={docId}
              backendOrigin={httpOriginOf(auth.wsUrl ?? '', '')}
              token={auth.token || undefined}
              theme={theme}
              showSidebar={showDataSidebar}
              sidebarWidth={dataSidebarWidth}
              setSidebarWidth={setDataSidebarWidth}
              schemaWidth={dataSchemaWidth}
              setSchemaWidth={setDataSchemaWidth}
            />
            </ErrorBoundary>
          </Suspense>
        )}
      </div>

      <div className="workflow-shell" style={{ display: appView === 'workflows' && canRemote ? 'flex' : 'none' }}>
        {visitedWorkflows && canRemote && (
          <Suspense fallback={<div className="code-panel-msg"><span>Loading Workflows tab…</span></div>}>
            <ErrorBoundary label="The Workflows tab">
            <WorkflowPanel
              ref={workflowPanelRef}
              projectId={docId}
              backendOrigin={httpOriginOf(auth.wsUrl ?? '', '')}
              token={auth.token || undefined}
              showRail={showWorkflowRail}
              railWidth={workflowRailWidth}
              setRailWidth={setWorkflowRailWidth}
              showGrid={wfShowGrid}
              showMinimap={wfShowMinimap}
              onOpenFile={openInCodeTab}
              codeTabs={codeTabs}
              initialOpenIds={savedWfTabs}
              initialActiveId={savedWfActive}
              subscribeWorkflowEvents={sim.onWorkflowEvent}
              onTabsChange={setWorkflowTabsMirror}
              onRunStateChange={setWfRunStateMirror}
              onHistoryChange={setWfHistory}
              triggerLinks={workflowTriggerLinks}
              bundleTriggers={sim.bundle?.workflowTriggers}
              triggerStatus={sim.triggerStatus}
            />
            </ErrorBoundary>
          </Suspense>
        )}
      </div>

      <div className="code-shell" style={{ display: appView === 'code' && canRemote ? 'block' : 'none' }}>
        {visitedCode && canRemote && (
          <Suspense fallback={<div className="code-panel-msg"><span>Loading Code tab…</span></div>}>
            <ErrorBoundary label="The Code tab">
            <CodePanel
              ref={codePanelRef}
              projectId={docId}
              projectAdapter={libraryAdapter as RemoteProjectAdapter}
              theme={theme}
              backendOrigin={httpOriginOf(auth.wsUrl ?? '', '')}
              token={auth.token || undefined}
              onDirtyChange={setCodeDirtyCount}
              onTabsChange={setCodeTabsMirror}
              initialTabs={savedCodeTabs}
              initialActivePath={savedCodeActive}
              showSidebar={showCodeSidebar}
              sidebarWidth={codeSidebarWidth}
              setSidebarWidth={setCodeSidebarWidth}
              onOpenWorkflow={canRemote ? openWorkflowTab : undefined}
              active={appView === 'code'}
            />
            </ErrorBoundary>
          </Suspense>
        )}
      </div>

      <main
        className={`app-main${viewSidebar ? '' : ' no-sidebar'}${viewGroups ? ' with-groups' : ''}`}
        style={{
          display: appView === 'view' ? 'grid' : 'none',
          gridTemplateColumns: viewSidebar && viewGroups
            ? `${Math.max(200, sidebarWidth)}px 1fr ${Math.max(150, groupsWidth)}px`
            : viewSidebar
              ? `${Math.max(200, sidebarWidth)}px 1fr`
              : viewGroups
                ? `1fr ${Math.max(150, groupsWidth)}px`
                : '1fr'
        }}
      >
        {viewSidebar && (
          <Sidebar
            bundle={sim.bundle}
            balance={sim.balance}
            metrics={sim.metrics}
            nodeLabel={nodeLabel}
            selectedNodeId={selectedNode}
            onSelectNode={(id) => {
              setSelectedNode(id);
              setSelectedEdge(null);
            }}
            canEdit={sim.canEdit}
            onAddMetric={sim.addMetric}
            onUpdateMetric={sim.updateMetric}
            onRemoveMetric={sim.removeMetric}
            onRenameMetric={sim.renameMetric}
            onSetLegend={sim.setLegend}
            onSetKnobs={sim.setKnobs}
            paramValues={sim.state?.lastTick?.params}
            tablesState={sim.tablesState}
            onLoadTables={sim.loadTables}
            onUpdateTableCell={
              sim.updateTableCell
                ? (table, rowId, column, value) => sim.updateTableCell!(table, rowId, column, value)
                : undefined
            }
            onSetParam={sim.setParam}
            groupBalance={sim.groupBalance}
            selectedGroupId={selectedGroup}
            onSelectGroup={(id) => {
              setSelectedGroup(id);
              setSelectedNode(null);
              setSelectedEdge(null);
            }}
          >
            <Resizer axis="x" dir={1} size={sidebarWidth} setSize={setSidebarWidth} />
          </Sidebar>
        )}
        {/* The canvas column: graph on top, tables docked below it. Tables
            render here (not as a grid track) so they sit at the bottom of the
            canvas column regardless of which other panels are open. */}
        <div className="canvas-col">
          <div className="canvas-wrap">
            <ErrorBoundary label="The graph canvas">
            <GraphCanvas
              ref={canvasRef}
              graph={sim.bundle.graph}
              tick={sim.state.lastTick}
              flowsFromModel={canRemote}
              workflowTriggerNodes={workflowTriggerNodes}
              workflowTriggerDisarmed={workflowTriggerDisarmed}
              workflowTriggerFlash={workflowTriggerFlash}
              overlays={sim.overlays}
              showOverlays={showOverlays}
              showArrows={showArrows}
              editable={sim.canEdit}
              selectedNodeId={selectedNode}
              selectedEdgeId={selectedEdge}
              nodeGroups={sim.bundle.nodeGroups}
              selectedGroupId={selectedGroup}
              onSelectGroup={(id) => {
                setSelectedGroup(id);
                setSelectedNode(null);
                setSelectedEdge(null);
                setMenu(null);
              }}
              onOpenGroup={(id) => {
                setOpenGroup(id);
                setSelectedGroup(id);
                setSelectedNode(null);
                setSelectedEdge(null);
                setOpenNode(null);
                setOpenEdge(null);
                setMenu(null);
              }}
              onUpdateGroup={sim.updateNodeGroup}
              onContextGroup={(id, pos) => openGroupMenu(id, pos)}
              onSelectNode={(id) => {
                setSelectedNode(id);
                setSelectedEdge(null);
                setMenu(null);
              }}
              onOpenNode={(id) => {
                setOpenNode(id);
                setSelectedNode(id);
                setSelectedEdge(null);
                setOpenEdge(null);
                setOpenGroup(null);
                setMenu(null);
              }}
              onSelectEdge={(id) => {
                setSelectedEdge(id);
                setSelectedNode(null);
                setMenu(null);
              }}
              onOpenEdge={(id) => {
                setOpenEdge(id);
                setEdgeModalTab('params');
                setSelectedEdge(id);
                setSelectedNode(null);
                setOpenNode(null);
                setMenu(null);
              }}
              onUpdateNode={sim.updateNode}
              onConnect={sim.addEdge}
              onReconnectEdge={(id, next) => sim.updateEdgeHandles?.(id, next)}
              onPromoteEdge={(id, sourceHandle, targetHandle, bends) => {
                sim.updateEdgeHandles?.(id, { sourceHandle, targetHandle });
                sim.setBends?.(id, bends);
              }}
              onAddBend={(id, x, y, idx, fallbackBends) => {
                // Insert a sharp-mode bend at the click position (already snapped
                // to the nearest point on the curve by the canvas) at the chain
                // index `idx` so it lands between its real neighbours. Adding a
                // bend does NOT open the edge modal.
                const e = sim.bundle!.graph.edges.find((q) => q.id === id);
                const current = e?.bends ?? fallbackBends ?? [];
                const at = Math.max(0, Math.min(current.length, idx));
                const bends = [
                  ...current.slice(0, at),
                  { x, y, mode: 'sharp' as const },
                  ...current.slice(at),
                ];
                sim.setBends?.(id, bends);
              }}
              onMoveBend={(id, idx, x, y, fallbackBends) => {
                const e = sim.bundle!.graph.edges.find((q) => q.id === id);
                const currentBends = e?.bends ?? fallbackBends;
                if (!currentBends) return;
                const bends = currentBends.map((b, i) => (i === idx ? { ...b, x, y } : b));
                sim.setBends?.(id, bends);
              }}
              onMoveLabel={(id, t) => sim.setLabelT?.(id, t)}
              onBendContext={(id, idx, x, y) => openBendMenu(id, idx, { x, y })}
              onContextPane={openPaneMenu}
              onContextNode={openNodeMenu}
              onContextEdge={openEdgeMenu}
              hiddenGroups={hiddenGroups}
              highlightedGroups={highlightedGroups}
              hoverGroup={hoverGroup}
              showGrid={showGrid}
              showMinimap={showMinimap}
              running={
                (sim.mode === 'api' || sim.mode === 'relay')
                  ? (sim.runStatus?.running ?? false)
                  : sim.status === 'running'
              }
            />
            </ErrorBoundary>
            {/* Sim errors (load failures, rejected ticks, engine faults) show
                inside the canvas — never as a full-screen block — so the rest
                of the UI stays usable while the error is visible. Errors clear
                on the next publish (play/pause/edit) or via the dismiss. */}
            {sim.error && (
              <div className="canvas-error" role="alert">
                <div className="canvas-error-head">
                  <strong>Graph error</strong>
                  <button
                    type="button"
                    className="canvas-error-dismiss"
                    onClick={() => sim.dismissError()}
                    title="Dismiss"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
                <pre>{sim.error}</pre>
              </div>
            )}
            {/* Replay tabs — read-only recorded periods overlaying the live
                canvas (kept mounted, display-gated per tab; the live canvas
                keeps ticking underneath, exactly like the Workflows-tab
                shells). Each panel owns its frozen GraphCanvas + the
                timeline bar that stands in for the StatusBar while active. */}
            {projectAdapter !== null &&
              replayTabs.map((tab) => (
                <div
                  key={tab.key}
                  className="replay-shell"
                  style={{
                    display:
                      appView === 'view' && activeReplayKey === tab.key ? 'flex' : 'none',
                  }}
                >
                  <ReplayPanel
                    doc={tab.doc}
                    projectId={docId}
                    adapter={projectAdapter}
                    liveBundles={sim.bundles ?? null}
                    showOverlays={showOverlays}
                    showArrows={showArrows}
                    showGrid={showGrid}
                    showMinimap={showMinimap}
                    showSidebar={showSidebar}
                    sidebarWidth={sidebarWidth}
                    setSidebarWidth={setSidebarWidth}
                    onSave={(doc) => saveReplayDoc(tab.key, doc)}
                    onSession={
                      activeReplayKey === tab.key ? registerActiveReplaySession : undefined
                    }
                  />
                </div>
              ))}
              {/* The old relay "POST synthetic event" dev tool is gone — the
                  "Listening for events" pulse lives in the StatusBar. */}
          </div>
          {viewTables && (sim.mode === 'api' || sim.mode === 'relay') && (
            <TablesPanel
              height={tablesHeight}
              tables={sim.tablesState}
              error={sim.tablesError}
              onClearError={sim.clearTablesError}
              onLoad={() => sim.loadTables?.() ?? Promise.resolve()}
              onUpdateCell={(table, rowId, column, value) =>
                sim.updateTableCell?.(table, rowId, column, value) ?? Promise.resolve()
              }
              onAddTable={(name, cols) => sim.addTable?.(name, cols) ?? Promise.resolve()}
              onAddRow={(table, cells) => sim.addTableRow?.(table, cells) ?? Promise.resolve()}
              onDeleteRow={(table, rowId) => sim.deleteTableRow?.(table, rowId) ?? Promise.resolve()}
              onToGraph={() => setShowFlows(true)}
            >
              <Resizer axis="y" dir={-1} size={tablesHeight} setSize={setTablesHeight} />
            </TablesPanel>
          )}
          {/* Table → graph (the docked tables panel's entry; the Data tab's
              records view opens its own). App owns this dialog because the
              panel is purely presentational. */}
          {showFlows && backendOrigin && (
            <FlowsDialog
              origin={{ origin: backendOrigin, token: auth.token || undefined, projectId: docId }}
              tables={sim.tablesState}
              onClose={() => setShowFlows(false)}
            />
          )}
        </div>
        {/* Groups panel — right rail. Toggled from the StatusBar. */}
        {viewGroups && (
          <GroupsPanel
            bundle={sim.bundle}
            hiddenGroups={hiddenGroups}
            highlightedGroups={highlightedGroups}
            hoverGroup={hoverGroup}
            onToggleHidden={(g) =>
              setHiddenGroups((cur) => {
                const next = new Set(cur);
                next.has(g) ? next.delete(g) : next.add(g);
                return next;
              })
            }
            onToggleHighlighted={(g) =>
              setHighlightedGroups((cur) => {
                const next = new Set(cur);
                next.has(g) ? next.delete(g) : next.add(g);
                return next;
              })
            }
            onHoverGroup={setHoverGroup}
            onToggleAll={(hide) => {
              if (hide) {
                // Hide every group currently in the bundle.
                const all = new Set<string>();
                for (const e of sim.bundle.graph.edges) if (e.group) all.add(e.group);
                setHiddenGroups(all);
              } else {
                setHiddenGroups(new Set());
              }
            }}
          >
            <Resizer axis="x" dir={-1} size={groupsWidth} setSize={setGroupsWidth} />
          </GroupsPanel>
        )}
      </main>

      {/* StatusBar — the bottom VSCode-style toggle rail. Replaces the old
          View menu: every view toggle (sidebar / groups / tables / overlays /
          grid / arrows / minimap / theme) is an icon button here. Right side
          carries the run status + connection + relay indicator. Hidden in the
          Code/Plot/Data views — those surfaces own their own controls — and
          in Workflows, which has its own bar (grid/minimap for the workflow
          canvas + run/worker dots). While a REPLAY tab is active the active
          panel's timeline bar IS the bottom bar (the same rule). */}
      {!replayActive && appView !== 'code' && appView !== 'plot' && appView !== 'data' && appView !== 'workflows' && (
      <StatusBar
        showGroupsPanel={showGroupsPanel}
        onToggleGroupsPanel={() => setShowGroupsPanel((x) => !x)}
        showTables={showTablesPanel}
        onToggleTables={() => setShowTablesPanel((x) => !x)}
        showOverlays={showOverlays}
        onToggleOverlays={() => setShowOverlays((x) => !x)}
        showGrid={showGrid}
        onToggleGrid={() => setShowGrid((x) => !x)}
        showArrows={showArrows}
        onToggleArrows={() => setShowArrows((x) => !x)}
        showMinimap={showMinimap}
        onToggleMinimap={() => setShowMinimap((x) => !x)}
        status={sim.status}
        step={sim.state.step}
        connectionStatus={sim.connectionStatus}
        apiLabel={auth.wsUrl ?? undefined}
        relay={sim.mode === 'relay'}
        onOpenDecisions={() => setShowDecisions(true)}
        onOpenMatrixCode={sim.bundle ? () => setShowMatrixCode(true) : undefined}
        onOpenTriggers={sim.bundle ? () => setShowTriggersPanel(true) : undefined}
        onOpenAgents={() => setShowAgentsPanel(true)}
        onOpenServices={canRemote ? () => setShowServices(true) : undefined}
      />
      )}
      <DecisionsModal
        isOpen={showDecisions}
        entries={sim.decisionLog}
        agentName={agentName}
        nodeLabel={nodeLabel}
        onClose={() => setShowDecisions(false)}
      />
      <MatrixCodegenModal
        isOpen={showMatrixCode}
        graph={sim.bundle?.graph}
        graphId={sim.activeGraphId ?? undefined}
        graphLabel={sim.bundle?.label}
        onClose={() => setShowMatrixCode(false)}
      />
      {showReplayPicker && canRemote && (
        <ReplayPicker
          runs={replayPickerRuns}
          replays={replayPickerReplays}
          loading={replayPickerLoading}
          error={replayPickerError}
          onOpenRun={(run) => openReplayTab(docFromRun(run))}
          onOpenReplay={(entry) => openReplayTab(docFromIndex(entry))}
          onClose={() => setShowReplayPicker(false)}
        />
      )}
      {appView === 'workflows' && canRemote && (
        <WorkflowStatusBar
          showGrid={wfShowGrid}
          onToggleGrid={() => setWfShowGrid((x) => !x)}
          showMinimap={wfShowMinimap}
          onToggleMinimap={() => setWfShowMinimap((x) => !x)}
          runStatus={wfRunState.runStatus}
          workerRunning={wfRunState.workerRunning}
        />
      )}

      {node && (
        <NodeModal
          node={node}
          bundle={sim.bundle}
          values={sim.state.lastTick.nodeValues[node.id]}
          flowsFromModel={canRemote}
          scope={buildNodeScope(node.id, sim.bundle, sim.state.lastTick)}
          editable={sim.canEdit}
          existingIds={sim.bundle.graph.nodes.map((n) => n.id)}
          libraryAdapter={libraryAdapter}
          projectAdapter={auth.wsUrl ? (libraryAdapter as RemoteProjectAdapter) : undefined}
          projectId={docId}
          onClose={() => setOpenNode(null)}
          onSetParam={(key, value) => sim.setParam(`${node.id}.${key}`, value)}
          bindingTools={canRemote
            ? {
              tables: sim.tablesState,
              onLoadTables: () => void sim.loadTables?.(),
              onUpdateTableCell: (t, r, c, v) => void sim.updateTableCell?.(t, r, c, v),
            }
            : undefined}
          onSetParamKey={(oldKey, newKey) => sim.setParamKey?.('node', node.id, oldKey, newKey)}
          onSetWorkflowTriggers={sim.setWorkflowTriggers}
          onFireWorkflow={canRemote ? fireTrigger : undefined}
          triggerStatus={sim.triggerStatus}
          recentTicks={sim.recentTicks}
          onUpdate={(n) => sim.updateNode?.(n)}
          onUpdateEdge={(e) => sim.updateEdge?.(e)}
          onRenameNode={(oldId, newId) => {
            const ok = sim.renameNode?.(oldId, newId) ?? false;
            if (ok) { setOpenNode(newId); setSelectedNode(newId); }
            return ok;
          }}
        />
      )}
      {group && (
        <GroupModal
          group={group}
          balance={sim.groupBalance[group.id]}
          metricValues={sim.groupMetrics[group.id]}
          editable={sim.canEdit}
          memberLabels={membersOf(group, sim.bundle.graph.nodes).map((n) => n.label)}
          existingIds={(sim.bundle.nodeGroups ?? []).map((g) => g.id)}
          onClose={() => setOpenGroup(null)}
          onUpdate={(g) => sim.updateNodeGroup?.(g)}
          onRenameGroup={(oldId, newId) => {
            const ok = sim.renameGroup?.(oldId, newId) ?? false;
            if (ok) { setOpenGroup(newId); setSelectedGroup(newId); }
            return ok;
          }}
        />
      )}
      {edge && edgeSource && edgeTarget && (
        <EdgeModal
          tab={edgeModalTab}
          onSwitchTab={setEdgeModalTab}
          edge={edge}
          sourceNode={edgeSource}
          targetNode={edgeTarget}
          allEdges={sim.bundle.graph.edges}
          knownGroups={
            // Distinct groups from bundle.groups + any edge.group actually in use.
            Array.from(new Set([
              ...(sim.bundle.groups ?? []),
              ...sim.bundle.graph.edges.map((e) => e.group).filter((g): g is string => !!g),
            ]))
          }
          libraryAdapter={libraryAdapter}
          projectAdapter={auth.wsUrl ? (libraryAdapter as RemoteProjectAdapter) : undefined}
          projectId={docId}
          currentFlow={sim.state.lastTick.edgeFlows[edge.id]}
          flowsFromModel={canRemote}
          scope={{
            x: sim.state.lastTick.edgeFlows[edge.id] ?? 0,
            params: Object.fromEntries(edge.params.map((p) => [p.key, p.value])),
            source: Object.fromEntries(edgeSource.params.map((p) => [p.key, p.value])),
            target: Object.fromEntries(edgeTarget.params.map((p) => [p.key, p.value])),
          }}
          editable={sim.canEdit}
          onClose={() => setOpenEdge(null)}
          onSetParam={(key, value) => sim.setParam(`e:${edge.id}.${key}`, value)}
          bindingTools={canRemote
            ? {
              tables: sim.tablesState,
              onLoadTables: () => void sim.loadTables?.(),
              onUpdateTableCell: (t, r, c, v) => void sim.updateTableCell?.(t, r, c, v),
            }
            : undefined}
          onUpdate={(e) => sim.updateEdge?.(e)}
          onRenameEdge={(oldId, newId) => {
            const ok = sim.renameEdge?.(oldId, newId) ?? false;
            if (ok) { setOpenEdge(newId); setSelectedEdge(newId); }
            return ok;
          }}
          onSetGroup={(id, g) => sim.setEdgeGroup?.(id, g)}
          onSetHandle={(id, which, h) =>
            sim.updateEdgeHandles?.(id, which === 'source' ? { sourceHandle: h } : { targetHandle: h })
          }
          onAddGroup={(g) => sim.addGroup?.(g)}
          onDelete={() => {
            // #9 — gate with ConfirmDialog, no window.confirm.
            confirm({
              title: 'Delete edge?',
              body: <>This removes the connection <code>{edge.id}</code>. This cannot be undone.</>,
              danger: true,
              confirmLabel: 'Delete edge',
            }).then((ok) => {
              if (ok) {
                sim.removeEdge?.(edge.id);
                setSelectedEdge(null);
              }
            });
          }}
        />
      )}

      {menu && <ContextMenu menu={menu} onClose={() => setMenu(null)} />}
      {showHelp && (
        <Modal title="VSKI LAB" onClose={() => setShowHelp(false)}>
          <div className="help-modal">
            <p>
              A runnable stock-and-flow modelling studio: build a graph of
              stocks and flows.
            </p>
            {/* The keyboard map — mirrors useGlobalKeys (the single global
                key layer). Keep the two in sync when bindings change. */}
            <h4>Keyboard shortcuts</h4>
            <dl className="help-keys">
              <dt>Undo / Redo</dt><dd>Ctrl/⌘+Z · Ctrl/⌘+Shift+Z</dd>
              <dt>Delete selection</dt><dd>Delete</dd>
              <dt>Duplicate</dt><dd>Ctrl/⌘+D</dd>
              <dt>Copy · Cut · Paste</dt><dd>Ctrl/⌘+C · X · V</dd>
              <dt>Select all</dt><dd>Ctrl/⌘+A</dd>
              <dt>Open selected</dt><dd>Enter</dd>
              <dt>Clear selection</dt><dd>Esc</dd>
              <dt>Fit view · Zoom</dt><dd>Ctrl/⌘+0 · +/−</dd>
              <dt>Ask (insert)</dt><dd>Ctrl/⌘+K</dd>
              <dt>Chat</dt><dd>Ctrl/⌘+/</dd>
              <dt>Library</dt><dd>Ctrl/⌘+Shift+P</dd>
              <dt>Save · Save As · Open</dt><dd>Ctrl/⌘+S · Shift+S · O</dd>
            </dl>
            <p className="help-version">Version 0.1.0</p>
          </div>
        </Modal>
      )}

      {/* Standalone AgentModal — opened from a node/edge context menu ("Agent"
          entry), independent of the NodeModal/EdgeModal. The host is built
          from the same data the in-modal agent button uses. */}
      {openAgentNode && (() => {
        const n = sim.bundle!.graph.nodes.find((x) => x.id === openAgentNode);
        if (!n) return null;
        const allParams = [...n.params, ...(n.gate?.params ?? [])];
        return (
          <AgentModal
            host={{
              title: n.label || n.id,
              paramOptions: allParams.map((p) => ({ key: p.key, label: p.label || p.key, path: `${n.id}.${p.key}` })),
              inputDescription:
                'All inflow edge data for this node is sent as input on every tick.',
            }}
            config={n.agentConfig}
            editable={sim.canEdit}
            libraryAdapter={libraryAdapter}
            onClose={() => setOpenAgentNode(null)}
            onChange={(cfg) => sim.updateNode?.({ ...n, agentConfig: cfg })}
          />
        );
      })()}
      {openAgentEdge && (() => {
        const e = sim.bundle!.graph.edges.find((x) => x.id === openAgentEdge);
        if (!e) return null;
        const src = sim.bundle!.graph.nodes.find((x) => x.id === e.source);
        const tgt = sim.bundle!.graph.nodes.find((x) => x.id === e.target);
        const conns = e.connections ?? [];
        return (
          <AgentModal
            host={{
              title: `${src?.label ?? e.source} → ${tgt?.label ?? e.target}`,
              paramOptions: e.params.map((p) => ({ key: p.key, label: p.label || p.key, path: `e:${e.id}.${p.key}` })),
              inputDescription:
                conns.length > 0
                  ? `The agent receives this edge's ${conns.length} connection(s) (${conns.map((c) => c.kind).join(', ')}) as input.`
                  : "The agent receives this edge's flow + param values as input.",
            }}
            config={e.agentConfig}
            editable={sim.canEdit}
            libraryAdapter={libraryAdapter}
            onClose={() => setOpenAgentEdge(null)}
            onChange={(cfg) => sim.updateEdge?.({ ...e, agentConfig: cfg })}
          />
        );
      })()}

      <LibraryModal
        isOpen={showLibraryModal}
        onClose={() => setShowLibraryModal(false)}
        adapter={libraryAdapter}
        onSelect={async (entry) => {
          setShowLibraryModal(false);
          const b = await libraryAdapter.get(entry.id);
          if (b && sim.replaceBundle) sim.replaceBundle(b);
        }}
      />

      {/* Run-as-task modal — opened from a file tab's context menu ("Run…").
          Listens for the `code:run-file` window event so the tab strip doesn't
          need a callback threaded through the Code panel. Backend task-config
          persistence is a planned follow-up (see Code-tab roadmap). */}
      <RunTaskModal path={runTask.path} onClose={runTask.close} />

      {/* Settings modal — opened from Menu → Settings…. Consolidates tick rate,
          theme, connection (with disconnect). Immediate-apply (no Apply button). */}
      <SettingsModal
        isOpen={showSettings}        onClose={() => setShowSettings(false)}
        theme={theme}
        onToggleTheme={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
        canReset={sim.state.step !== 0}
        onReset={() => sim.reset()}
        hz={hz}
        onSetHz={setHz}
        stopServicesOnClose={canRemote ? stopServicesOnClose : undefined}
        onSetStopServices={canRemote ? setStopServicesOnClose : undefined}
        workflowsAutostart={workflowsAutostart}
        onSetWorkflowsAutostart={
          canRemote && docId && libraryAdapter instanceof RemoteProjectAdapter
            ? (v) => {
                setWorkflowsAutostart(v);
                libraryAdapter.setWorkflowAutostart(docId, v).catch(() => {
                  setWorkflowsAutostart(!v); // best-effort revert on failure
                });
              }
            : undefined
        }
        connectionStatus={sim.connectionStatus}
        apiLabel={auth.wsUrl ?? undefined}
        connectionBackendOrigin={canRemote ? backendOrigin : null}
        onDisconnect={auth.wsUrl ? () => {
          auth.setToken(null);
          auth.setWsConnection(null, false);
          navigate('/');
        } : undefined}
        storageBackendOrigin={canRemote ? backendOrigin : null}
        storageToken={auth.token}
        cronProjectId={canRemote && docId ? docId : undefined}
        cronBackendOrigin={canRemote ? backendOrigin : null}
        cronToken={auth.token}
        aiConfig={aiConfig}
        aiProvider={aiProvider}
        aiModel={aiModel}
        onSetAiProvider={setAiProvider}
        onSetAiModel={setAiModel}
        aiBackendOrigin={canRemote ? backendOrigin : null}
        aiToken={auth.token}
        onReloadAi={reloadAi}
        onInitAgentInstructions={
          canRemote && docId && libraryAdapter instanceof RemoteProjectAdapter
            ? () => libraryAdapter.initAgentInstructions(docId)
            : undefined
        }
        version={appVersion}
      />

      {/* Chat panel (backend-gated, lazy-loaded — assistant-ui chunks only land
          when the user opens chat). The provider/model come from the persisted
          AI prefs (shared with SettingsModal + AskBox). */}
      {canRemote && (
        <Suspense fallback={null}>
          <ChatModal
            isOpen={showChat}
            onClose={() => setShowChat(false)}
            backendOrigin={backendOrigin ?? ''}
            token={auth.token}
            projectId={docId}
            workspaceContext={chatWorkspaceContext}
            provider={aiChoice?.provider}
            model={aiChoice?.model}
            aiConfig={aiConfig}
            aiProvider={aiProvider}
            aiModel={aiModel}
            onProvider={setAiProvider}
            onModel={setAiModel}
          />
        </Suspense>
      )}

      {/* Floating AskBox (Cmd/Ctrl+K). Cheap; always mounted when canRemote so
          the shortcut is live in every surface. Captures the active editor's
          InsertTarget on open. */}
      {canRemote && (
        <AskBox
          open={showAsk}
          onClose={() => setShowAsk(false)}
          backendOrigin={backendOrigin ?? ''}
          token={auth.token}
          projectId={docId}
          provider={aiChoice?.provider}
          model={aiChoice?.model}
        />
      )}

      {/* Download format picker — Project ZIP (when connected) or Bundle JSON.
          Fires the matching sim action on select. */}
      <FormatPicker
        isOpen={downloadPickerOpen}
        title="Download"
        options={downloadOptions}
        onSelect={(id) => {
          setDownloadPickerOpen(false);
          if (id === 'project-zip') void sim.downloadProject?.();
          else if (id === 'bundle-json') {
            const b = sim.exportBundle?.();
            if (!b) return;
            const blob = new Blob([JSON.stringify(b, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${b.id ?? 'file'}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
          }
        }}
        onClose={() => setDownloadPickerOpen(false)}
      />

      {/* Export format picker — training-data exports from the backend's
          capture/replay (CSV wide/long, trajectory JSONL). */}
      <FormatPicker
        isOpen={exportPickerOpen}
        title="Export run"
        options={exportOptions}
        onSelect={(id) => {
          setExportPickerOpen(false);
          if (id === 'csv-wide') void sim.exportRun?.('wide');
          else if (id === 'csv-long') void sim.exportRun?.('long');
          else if (id === 'trajectory') void sim.exportRun?.('trajectory');
        }}
        onClose={() => setExportPickerOpen(false)}
      />

      {/* Global-agents panel (Phase D). Bundle-level agents manager — lists
          bundle.agents[] and edits each via AgentModal in scope='global'.
          Writes go through replaceBundle since `agents` is a bundle field. */}
      {showAgentsPanel && sim.bundle && (
        <AgentsPanel
          bundle={sim.bundle}
          editable={sim.canEdit}
          libraryAdapter={libraryAdapter}
          projectAdapter={
            auth.wsUrl ? (libraryAdapter as RemoteProjectAdapter) : undefined
          }
          projectId={docId}
          onClose={() => setShowAgentsPanel(false)}
          onChange={(agents) => {
            if (sim.replaceBundle) sim.replaceBundle({ ...sim.bundle!, agents });
          }}
        />
      )}

      {/* ⚡ Trigger management (the trigger contract's UI half). The whole
          management surface for workflowTriggers: condition-builder add/edit,
          arm/disarm, dry-run over captured ticks, fire-now, live status. */}
      {showTriggersPanel && sim.bundle && (
        <TriggersPanel
          bundle={sim.bundle}
          editable={sim.canEdit}
          projectAdapter={auth.wsUrl ? (libraryAdapter as RemoteProjectAdapter) : undefined}
          projectId={docId}
          recentTicks={sim.recentTicks}
          triggerStatus={sim.triggerStatus}
          fireTrigger={fireTrigger}
          onClose={() => setShowTriggersPanel(false)}
          onSetWorkflowTriggers={sim.setWorkflowTriggers}
        />
      )}

      {/* Services modal — the MenuBar slot + StatusBar docker button open it,
          scoped to THIS project's task containers. */}
      {showServices && (
        <ServicesModal
          backendOrigin={canRemote ? backendOrigin : null}
          token={auth.token}
          projectId={docId}
          onClose={() => setShowServices(false)}
        />
      )}

      {/* The in-app updater (the version checker's Update modal). */}
      {showUpdate && <UpdateModal onClose={() => setShowUpdate(false)} />}

      {/* Close-project flow: ask about the project's docker services (only
          when it has running ones). "Remember choice" lands in Settings →
          Engine ("stop services on close"); after the answer the project
          closes. */}
      {showStopDialog && (
        <StopServicesDialog
          backendOrigin={canRemote ? backendOrigin : null}
          token={auth.token}
          projectId={docId}
          onChoice={(_stop, remember) => {
            if (remember) setStopServicesOnClose(_stop);
            setShowStopDialog(false);
            sim.reset();
            navigate('/');
          }}
        />
      )}

    </div>
    </ReactFlowProvider>
  );
}

// The NodeModal live-eval scope builder lives in graph/scope.ts (shared with
// the read-only replay modals, which overlay the RECORDED effective params).
